import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { UNIT_PLACEHOLDER } from "Utilities/Address/AddressValidation/providers/smarty/smartyAutocomplete";

import buildBareBuildingCommit from "../logic/bareBuildingCommit";
import buildBaseRetainCommit from "../logic/baseRetainCommit";
import {
  addressNeedsUnit,
  candidateConsistentWithForm,
  pickTopCandidate,
  valueConsistent,
} from "../logic/candidateSelection";
import {
  buildUndoPayload,
  savedDiffersFromSeen,
} from "../logic/standardizationDiff";
import {
  abbreviationAwarePrefixMatch,
  addr1Equivalent,
  deriveShadowSuffix,
  extendsTrailingNumber,
  streetExtendsPastLine,
} from "../logic/streetMatching";

import useDeliverabilityVerdict from "./useDeliverabilityVerdict";
import useSmartyCandidates, {
  DEBOUNCE_MS,
  STREET_REGEX,
} from "./useSmartyCandidates";
import useStaleSubfieldGhosting from "./useStaleSubfieldGhosting";

const VERIFICATION_PROVIDER = "smarty";

// `plus4` stays in the value-level subfield list because it round-trips
// through the verify endpoint and the backend — but Smarty's US
// Autocomplete Pro never returns it, so the shadow / keydown /
// fieldRefs machinery below intentionally omits plus4. The plus4 input
// belongs on admin verify-flow surfaces only; it's not an autocomplete
// concept.
const ADDRESS_SUBFIELDS = ["addr1", "city", "state", "zip", "plus4", "country"];
// plus4 has no input — it's server-enriched, never user-typed. Its index in
// ADDRESS_SUBFIELDS is excluded from the user-edit detection so a plus4-only
// value change (e.g. a verified save's backend response adding ZIP+4) is not
// read as an edit that would strip verification.
const PLUS4_SUBFIELD_INDEX = ADDRESS_SUBFIELDS.indexOf("plus4");
// Fields that get a per-field shadow + per-field commit. State is included
// even though it's a dropdown — consumers render a ghost preview and accept
// it via blur/right-arrow. Keydown auto-wiring only covers text inputs
// (see KEYDOWN_FIELDS); state is wired by the StateField consumer.
const SHADOW_FIELDS = ["addr1", "city", "state", "zip"];
const KEYDOWN_FIELDS = ["addr1", "city", "zip"];
const EMPTY_SHADES = { addr1: "", city: "", state: "", zip: "" };

const stripMeta = (obj) => ({
  ...obj,
  isVerified: false,
  verificationProvider: "",
  verifiedAt: "",
});

// Multi-unit autofill writes a placeholder slot into addr1 (e.g.
// "1 Pennsylvania Pl Apt ___") so the user can finish the address by
// typing a unit number. While the sentinel is present the address is
// NOT really verified — Smarty has confirmed the building, not the
// unit — so writeMeta withholds isVerified and the timestamp. The
// fields are still written; the user keeps the partial address and the
// address stays unverified until they fill the slot (typing changes addr1 →
// fetch effect re-fires → new autofill).
const writeMeta = (obj) => {
  const addr1Incomplete =
    typeof obj.addr1 === "string" && obj.addr1.includes(UNIT_PLACEHOLDER);
  if (addr1Incomplete) {
    return {
      ...obj,
      isVerified: false,
      verificationProvider: "",
      verifiedAt: "",
      validationSkipped: "",
    };
  }
  return {
    ...obj,
    isVerified: true,
    verificationProvider: VERIFICATION_PROVIDER,
    verifiedAt: new Date().toISOString(),
    // Verification supersedes any prior skip — clear so the verified flag
    // doesn't coexist with a stale skip when an address verifies after having
    // been left unverifiable.
    validationSkipped: "",
  };
};

// Build a snapshot of current address subfield values.
// Used when capturing the user-typed values as the undo baseline.
const snapshotCurrentSubfields = (value) =>
  ADDRESS_SUBFIELDS.reduce((acc, fieldName) => {
    acc[fieldName] = value[fieldName] || "";
    return acc;
  }, {});

// The candidate's city/state/zip the user SAW ghost in but never committed —
// what an inline per-field accept's undo baseline must add (the caller spreads
// `...value` first, so the user's own typed locality already stands). Skip a
// field the user TYPED that contradicts the candidate, leaving the spread
// `...value` to keep it (the candidate never overwrites what the user typed),
// so the undo baseline can't trip the street-only standardization gate on a
// locality field.
const candidateLocalityFill = (candidate, value, editedFields) =>
  ["city", "state", "zip"].reduce((acc, fieldName) => {
    const candidateFieldValue = candidate[fieldName];
    const typedContradiction =
      editedFields[fieldName] &&
      !valueConsistent(value[fieldName], candidateFieldValue);
    if (candidateFieldValue && !typedContradiction)
      acc[fieldName] = candidateFieldValue;
    return acc;
  }, {});

/**
 * Drives per-field shadow-text autocomplete, per-field commit on blur/Enter/→,
 * verify-on-group-blur, and the sticky auto-verification-off breaker for a
 * controlled 5-field address form.
 *
 * @param {Object} params
 * @param {import("./types").AddressValue} params.value
 * @param {(delta: Partial<import("./types").AddressValue>) => void} params.onChange
 * @param {React.MutableRefObject<HTMLInputElement>} params.addr1Ref
 * @param {React.MutableRefObject<HTMLInputElement>} params.cityRef
 * @param {React.MutableRefObject<HTMLInputElement>} params.zipRef
 * @param {React.MutableRefObject<HTMLElement>} params.containerRef
 * @param {boolean} [params.enabled=true]
 * @param {boolean} [params.isDirty]
 * @param {boolean} [params.holdTypedValues=false] - when true, the hook never
 *   autonomously *commits* canonical values into the visible fields while
 *   editing: a plain per-field blur and the auto group-blur autofill are
 *   suppressed, so the inputs keep what the user typed until an *explicit*
 *   commit (Save / keyboard accept incl. Tab / Enter).
 *   Surfaces that present a read-only view after save (AddressInput) use this
 *   so the commit-time standardization (e.g. "highway" → "hwy") appears only in
 *   that view, never as a mid-edit flash. It does NOT suppress the live
 *   *previews* — per-field shadow text and the stale-subfield soft-overwrite
 *   ghosting still run, because a ghost reverts if untouched and the consumer
 *   captures the Save-time autofill separately. Defaults off — every other
 *   surface also commits on a plain blur.
 * @param {boolean} [params.verifyDeliverability=false] Opt-in deliverability
 *   gate for the USPS-Verified badge, set only by the OPTIMISTIC surfaces
 *   (public signups, family registration, giving, sacrament) whose badge isn't
 *   backend-corrected by a save. When true, an in-session leaf verification only
 *   shows the badge once the backend US Street DPV check confirms the address
 *   DELIVERABLE. Off → today's behavior (badge from the autocomplete leaf alone).
 */
export default function useAddressAutocomplete({
  value,
  onChange,
  addr1Ref,
  cityRef,
  zipRef,
  containerRef,
  enabled = true,
  isDirty,
  holdTypedValues = false,
  verifyDeliverability = false,
}) {
  // True once the user has TYPED into the city field this session. Only a typed
  // city is composed into Smarty's `search` — a pre-filled STALE city (the
  // "updating an old address" flow) must not pollute the street query (real
  // Smarty returns nothing for "1600 Pennsylvania Sciota"). Kept in sync below
  // from the stale-subfield hook's edited-set, the same signal the consistency
  // gate uses to decide a candidate may override a stale city but never a typed
  // one. Resets with that set on commit.
  const cityEditedRef = useRef(false);
  // Set true when an autofill (chip click, group-blur)
  // commits a partial address containing the UNIT_PLACEHOLDER sentinel.
  // The effect below watches value.addr1 — once React flushes the
  // committed value into the input, we focus addr1 and select the
  // sentinel so the user's first keystroke replaces it.
  const pendingSlotSelectRef = useRef(false);

  useEffect(() => {
    if (!pendingSlotSelectRef.current) return;
    if (!value.addr1 || !value.addr1.includes(UNIT_PLACEHOLDER)) return;
    const input = addr1Ref && addr1Ref.current;
    if (
      !input ||
      typeof input.setSelectionRange !== "function" ||
      !input.isConnected
    ) {
      // No live input handle (admin-less surface, jsdom remount corner).
      // Clear the flag so a later mount doesn't claim focus retroactively.
      pendingSlotSelectRef.current = false;
      return;
    }
    const idx = input.value.lastIndexOf(UNIT_PLACEHOLDER);
    if (idx < 0) return;
    input.focus();
    input.setSelectionRange(idx, idx + UNIT_PLACEHOLDER.length);
    pendingSlotSelectRef.current = false;
  }, [value.addr1, addr1Ref]);
  // Group-blur fired while a fetch was still in flight: resolveOnGroupBlur
  // can't make a correct decision (zero candidates might be "not yet" or
  // "actually nothing"). The fetch completion re-runs resolution.
  const pendingResolveRef = useRef(false);
  // Set when resolveOnGroupBlur strips a committed unit sentinel ("…Unit ___" →
  // "…Unit") and settles the address unverified. The strip writes addr1, which
  // would otherwise trip the subfield-change effect's "user edited while
  // unverifiable → clear the verdict" rule and wipe the validationSkipped="1"
  // the strip just set. This flag tells that effect the addr1 change is the
  // hook's own strip, not a user edit, so it leaves the verdict standing.
  const slotStripWriteRef = useRef(false);
  // Set when resolveOnGroupBlur commits a base-retain canonicalization
  // ("…Road apt 4" → "…Rd apt 4" + filled locality, settling unverifiable). That
  // addr1 + locality write is the hook's own, not a user edit — without this the
  // subfield-change effect would read it as "user edited while unverifiable" and
  // clear the validationSkipped="1" the commit just set. Mirrors
  // slotStripWriteRef.
  const baseRetainWriteRef = useRef(false);
  const resolveOnGroupBlurRef = useRef(() => {});
  // Stable refs for values read by the subfield-change effect but not used
  // as its triggers. Updated every render so the effect always sees current
  // values without those values expanding the trigger dep list.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Current committed value, read by `reset({ deferred: false })` (Save) to
  // re-baseline against the value that was just persisted.
  const valueRef = useRef(value);
  valueRef.current = value;
  const topCandidateRef = useRef(null);
  const verificationBaselineRef = useRef(null);
  const isUnverifiableRef = useRef(false);
  const isSkippedRef = useRef(false);
  const isVerifiedRef = useRef(false);
  const autoVerificationOffRef = useRef(false);
  // The canonical, UNIT-FREE street (`candidate.streetLine`) the engine wrote
  // when an in-session autofill auto-formatted the street — i.e. the verified
  // street is NOT the literal start of what the user typed (a visible
  // standardization OR a silent forward-ghost / abbreviation completion). It is
  // the reject discriminator: a later edit that breaks this prefix means the
  // user is editing the canonical the engine imposed → trip the breaker. Stored
  // unit-free so correcting a UNIT never breaks the prefix. Null until the
  // engine canonicalizes a street; cleared at every verification teardown
  // (clear, undo, base-retain/bare-building/slot-strip, and the reject trip).
  const canonicalizedStreetRef = useRef(null);

  // Set by `reset()` (a consumer's Cancel) so the very next subfield-change
  // effect treats the consumer's value revert as a clean re-seed, NOT a user
  // edit. Without it the revert from an in-session canonical back to the
  // original street reads as "edited the canonical away" and trips the reject
  // breaker (`canonicalizedStreetRef` is still armed when the value flips),
  // leaving autocomplete dead on the next edit. The flag is consumed by the
  // effect, which re-snapshots the baseline from the restored value.
  const resetPendingRef = useRef(false);

  // Verification baseline: snapshot of subfield values at the moment
  // verification was last established (either pre-rendered with
  // isVerified=true on mount, or written by an autofill/pick). Used by the
  // subfield-change effect to suppress a redundant strip when a change
  // event fires but the value still equals the baseline, and by the
  // multi-unit UNIT_PLACEHOLDER guard. Cleared on Undo.
  const [verificationBaseline, setVerificationBaseline] = useState(() =>
    value.isVerified === true ? snapshotCurrentSubfields(value) : null
  );

  // System verdict: a resolve attempt (auto or explicit) found zero
  // candidates for the typed address. Drives the "Unverifiable" badge —
  // status label + retry icon. Cleared when the user edits a subfield (the
  // verdict is tied to the prior input), when an autofill or pick
  // succeeds, or when the user explicitly re-engages.
  //
  // Seeded from a mounted value already carrying validationSkipped="1", but ONLY
  // on hold (popover) surfaces. Those reopen by REMOUNTING the editor fresh on
  // the committed value (boards/Transfers cell popovers): without this seed the
  // editor mounts with isUnverifiable=false while the value is skipped, and the
  // subfield-change effect's edit-clear (which fires only when isUnverifiable is
  // set — deliberately, so it never clobbers the undo verdict) would never
  // re-enable the fetch, so the address could never re-verify on edit (the
  // reported Transfers bug). Seeding puts the reopened address in the SAME state
  // an in-session unverifiable commit produces, so a user edit re-enables
  // verification — mirroring the verified-edit path. Gated to hold surfaces so
  // inline editors keep the documented "seeded-skip renders inert (no callout)
  // until touched" contract (their UnverifiableCallout reads isUnverifiable; the
  // popover surfaces drive their callout from the post-save slide, not this).
  const [isUnverifiable, setIsUnverifiable] = useState(
    () => Boolean(holdTypedValues) && value.validationSkipped === "1"
  );

  // Sticky "auto-verification is off" breaker — the undo→re-collapse guard.
  // Tripped by EXACTLY ONE gesture: the user clicks undo on the
  // standardization callout (rejects the formatter). Without it, undo would
  // restore the typed text and the engine would immediately re-collapse to the
  // same candidate; tripping the breaker is what makes "use what I typed"
  // stick. While set it gates `canFetch`, so shadow/autocomplete/verify all
  // stop and the field behaves like a plain input. It re-enables on EXACTLY
  // ONE gesture: the user clears the address field entirely (handled in the
  // subfield-change effect). Nothing else trips or resets it — editing a
  // previously-verified address does NOT trip it (verification stays on and
  // keeps helping).
  const [autoVerificationOff, setAutoVerificationOff] = useState(false);

  // Bare-building intent — the narrow breaker, sibling of base-retain. Tripped
  // when the user engages a multi-unit slot ("…Apt ___") and then deletes addr1
  // back down to the bare building street (Path 2). It suppresses ONLY the unit
  // affordance (the unit ghost + the Add-unit button) so the user isn't
  // re-nudged toward a unit they've abandoned. It deliberately does NOT gate
  // `canFetch` — a manually-typed unit must still verify. It governs LIVE
  // suppression only; the actual bare-building commit is driven by the
  // street-equivalence check in resolveOnGroupBlur, so a detection miss degrades
  // to a re-nudge, never a wrong save. Resets when addr1 is cleared or the
  // multi-unit building changes (a different candidate.streetLine).
  const [bareBuildingIntent, setBareBuildingIntent] = useState(false);
  const bareBuildingIntentRef = useRef(false);
  bareBuildingIntentRef.current = bareBuildingIntent;
  // Set whenever a multi-unit placeholder slot is painted into addr1 (Tab /
  // autofill / Add-unit). Sticky signal that the user engaged a unit slot, so a
  // later delete-back-to-bare-street can be recognized as Path-2 intent.
  const slotEngagedRef = useRef(false);
  // The hook's own bare-building commit write (addr1 + locality, settling
  // unverifiable). Mirrors baseRetainWriteRef — tells the subfield-change effect
  // the addr1 change is ours, not a user edit, so it leaves the verdict standing.
  const bareBuildingWriteRef = useRef(false);

  // The address as the user *saw* it (typed text + ghost) at the moment a
  // collapse-in-place autofill standardized it beyond that — the undo payload.
  // Null when the last autofill matched what the user saw (no surprise) or on
  // hold surfaces (which compute their own standardization at Save). Drives the
  // standardization undo callout; `onUndoStandardization` re-persists it.
  const [standardization, setStandardization] = useState(null);
  const standardizationRef = useRef(null);
  standardizationRef.current = standardization;

  // What the user had typed (raw, no canonicalization) at the moment they
  // began leaving the address fields — captured by the FIRST per-field commit
  // in a group-leave sequence, BEFORE it rewrites the field to the canonical
  // form. The group resolve reads this as the "seen" baseline so a
  // standardization surprise (e.g. "highway" → "Hwy") is detected even when a
  // plain blur canonicalized the field first (the per-field blur runs before
  // the group focusout). Null between sequences; consumed (cleared) by each
  // terminal resolve. Enter resolves before any blur, so it falls back to the
  // live value snapshot, which is still the typed text.
  const seenBeforeCommitRef = useRef(null);

  // A fresh edit session (enabled false→true) clears the stale Unverifiable
  // verdict so a re-opened address doesn't show last session's verdict before
  // a new fetch settles. The breaker and the rest of the per-session state are
  // deliberately NOT reset HERE: this generic enabled-transition handler can't
  // know whether the consumer is reverting the buffer (Cancel) or committing it
  // (Save), and the two need different re-seed timing. Ending a session and
  // clearing that state is the CONSUMER's job, via `reset()` — see AddressInput
  // (the only `enabled`-toggling surface), whose hook stays mounted across edit
  // sessions and calls `reset()` on reopen so the breaker/canonical-street ref/
  // baseline/stale-subfield latches don't leak from an abandoned session into
  // the next. (At the hook level the breaker still resets only on a full clear,
  // its single internal reset gesture.)
  // Track EVERY transition (not just false→true): recording only the rising
  // edge left `prevEnabled` stuck true after a true→false toggle, making every
  // later re-enable invisible (the stale verdict survived a close-then-reopen
  // edit session on AddressInput, the one surface that toggles `enabled`).
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  if (enabled !== prevEnabled) {
    setPrevEnabled(enabled);
    if (enabled) setIsUnverifiable(false);
  }

  const addr1 = value.addr1 || "";
  const city = value.city || "";
  const isVerified = value.isVerified === true;
  const isSkipped = value.validationSkipped === "1";
  // Implicit-skip behaviors (no fetch, no group-blur resolve) block flow.
  // Any skipped state — pre-rendered skip or an unverifiable verdict — sets
  // validationSkipped="1" and makes autocomplete go genuinely inactive (no
  // fetch, no ghost, no group-blur resolve). An undo additionally trips the
  // breaker, which clears only on a full clear of the address.
  const skipBlocksFlow = isSkipped;

  // Keep stable refs in sync so the subfield-change effect can read
  // current values without them expanding its trigger dep list.
  verificationBaselineRef.current = verificationBaseline;
  isUnverifiableRef.current = isUnverifiable;
  isSkippedRef.current = isSkipped;
  isVerifiedRef.current = isVerified;
  autoVerificationOffRef.current = autoVerificationOff;

  // Caller can provide a precise dirty signal (e.g. react-hook-form's
  // dirtyFields). When omitted, fall back to "any subfield has a value",
  // which is correct for blank-slate forms but treats pre-filled, untouched
  // addresses as dirty.
  const addressIsDirty = useMemo(
    () =>
      typeof isDirty === "boolean"
        ? isDirty
        : Boolean(value.addr1) ||
          Boolean(value.city) ||
          Boolean(value.state) ||
          Boolean(value.zip) ||
          Boolean(value.plus4) ||
          Boolean(value.country),
    [
      isDirty,
      value.addr1,
      value.city,
      value.state,
      value.zip,
      value.plus4,
      value.country,
    ]
  );

  // We only support autocomplete/verification for US addresses; every other
  // country behaves exactly like production today — a plain text input, no
  // chrome and no verdict. Like the breaker, this turns the WHOLE flow off, so
  // it gates all three load-bearing sites (`canFetch`, `onFieldBlur`, and
  // `resolveOnGroupBlur`). Gating only `canFetch` is not enough: that suppresses
  // the fetch (hence suggestions/ghosts/liveUnverifiable), but `resolveOnGroupBlur`
  // is gated separately and would still fall through to the `!acceptable`
  // Unverifiable landing on group-blur, stamping validationSkipped="1" + popping
  // the callout. The provider layer (`smartyAutocomplete`) returns [] for non-US
  // as a defensive backstop, but it can't stop the commit path.
  const isUSCountry = (value.country || "US") === "US";

  const canFetch =
    enabled &&
    !isVerified &&
    !skipBlocksFlow &&
    // The breaker (tripped only by an undo of a standardization) turns the
    // whole flow off for this edit-open: no fetch → no shadow → no re-collapse
    // to the candidate the user just rejected. It re-enables only when the
    // address is cleared entirely (the subfield-change effect resets it).
    !autoVerificationOff &&
    isUSCountry &&
    addressIsDirty &&
    STREET_REGEX.test(addr1);

  const country = value.country || "US";
  const {
    allCandidates,
    fetchInFlight,
    fetchErrored,
    lastNonEmptyCandidatesRef,
    retainedCandidates,
  } = useSmartyCandidates({ addr1, city, country, canFetch, cityEditedRef });

  // Smarty errored (4xx/5xx/network) rather than returning a result — an
  // outage. Make verification INVISIBLE: like the non-US and breaker OFF
  // states, gate the commit paths (`onFieldBlur`, `resolveOnGroupBlur`) and
  // the live callout (`liveUnverifiable`) so we show no "couldn't verify"
  // chrome and stamp NO verdict (no validationSkipped="1"). Sending no skip
  // marker is deliberate: it lets the backend's own deliverability check still
  // verify the address on save.
  //
  // UNLIKE non-US/breaker, this deliberately does NOT gate `canFetch`. Those
  // are stable conditions (country, an explicit rejection) where you never want
  // to fetch; an outage is transient. Leaving the fetch enabled means the next
  // keystroke retries and the flow recovers on its own when Smarty returns, and
  // gating it here would self-stick (the error would disable the query that
  // clears the error). A fresh outage has no candidates, so nothing renders
  // anyway; the only sites that would mis-fire are the commit/callout ones,
  // which is exactly what we gate.
  const autocompleteUnavailable = fetchErrored;

  // --- Pick the candidate that best fits what the user has typed --------
  // The selection logic (forward prefix matches, shell exclusion, the
  // geo-then-prevalence two-stage pick, the exact retained match, and the
  // base + appended-tail fallback) lives in `pickTopCandidate`
  // (candidateSelection.js).

  const committedCity = value.city;
  const committedState = value.state;
  const committedZip = value.zip;
  const { topCandidate, appendedTail } = useMemo(
    () =>
      pickTopCandidate({
        typed: addr1 || "",
        allCandidates,
        retainedCandidates,
        committedCity,
        committedState,
        committedZip,
      }),
    [
      allCandidates,
      addr1,
      committedCity,
      committedState,
      committedZip,
      retainedCandidates,
    ]
  );
  topCandidateRef.current = topCandidate;

  // `topCandidate` is the single concept the flow runs on: the candidate
  // that matches what the user typed (abbreviation-aware street prefix,
  // best-scored on committed city/state/zip). It drives the ghosts, the
  // multi-unit fill button, and the group-blur autofill. There is no separate
  // "a lone suggestion is special" path — the count of candidates is not a
  // trust signal; a candidate that *matches the user's input* is. A lone
  // suggestion whose street the user's text doesn't match is therefore NOT
  // auto-accepted — it resolves to the unverifiable verdict like any other
  // unmatched result. (Multi-unit `entries > 1` candidates still flow
  // through here; their `___` sentinel gates isVerified in writeMeta.)

  // --- Per-field shadow suffixes -----------------------------------------
  // For each text field, expose the portion of the top candidate's value
  // for that field that extends beyond what the user has typed. Empty
  // string when there's no ghost (mismatch, no candidate, verified, etc.).

  // All fields follow `topCandidate` — the candidate matching what the
  // user typed. A street ghost that disagreed with the typed text would
  // read as a bug, and the city/state/zip ghosts preview that same row.

  // Once the user types PAST the bare street into the unit slot, the unit is
  // theirs to fill — autocomplete can't help someone enter their own apartment
  // or suite number, and a ghost here only misleads (a lingering placeholder
  // used to tempt users into committing before the verification fetch landed,
  // saving an address unverified). `enteredUnitSlot` is the signal: it kills the
  // addr1 ghost entirely while a unit is being typed (and gates the Add-unit
  // button below). Verification still resolves silently from the fetch — we
  // simply don't ghost the unit.
  //
  // It LATCHES on the engaged building's `streetLine`: once the typed addr1
  // extends past a known multi-unit building's bare street, the latch holds even
  // after Smarty narrows the pool down to a single leaf (which drops the
  // multi-unit candidate this keys on — without the latch the forward completion
  // would snap back, e.g. "Ste 13" → ghost "00"). The latch drops on its own
  // when addr1 retreats to/below the bare street or moves to a different street
  // (the anchor no longer prefix-matches) — so a full clear or building switch
  // resets it with no external bookkeeping. Reads `lastNonEmptyCandidatesRef`
  // because `topCandidate` is null across the per-keystroke fetch gap.
  const unitSlotBuildingRef = useRef("");
  const enteredUnitSlot = useMemo(() => {
    const typed = value.addr1 || "";
    // Fresh detection: the typed street extends past a multi-unit building's bare
    // street into the slot. Remember the building so the latch survives narrowing.
    const building = lastNonEmptyCandidatesRef.current.find(
      (candidate) =>
        addressNeedsUnit(candidate) &&
        streetExtendsPastLine(typed, candidate.streetLine)
    );
    if (building) {
      unitSlotBuildingRef.current = building.streetLine;
      return true;
    }
    // Latched: still extending past the same building (the pool may now hold only
    // the leaf, which is no longer flagged multi-unit).
    if (streetExtendsPastLine(typed, unitSlotBuildingRef.current)) return true;
    unitSlotBuildingRef.current = "";
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lastNonEmptyCandidatesRef is a stable ref; allCandidates is the re-run trigger via which the ref gets updated
  }, [value.addr1, allCandidates, lastNonEmptyCandidatesRef]);

  // Live clear-stash-restore of stale city/state/zip: when the top candidate
  // would *replace* a stale value (one it can't render as a ghost suffix), the
  // field is cleared so the candidate shows as a full-field ghost, and the
  // original is stashed for restore. Extracted into its own hook; it returns
  // the reset callback the commit paths fire so a stash can't outlive a
  // verification. Placed ABOVE `shadows` so the ghost gate can read its
  // edited-subfield set (`editedSubfieldsRef`) to suppress a contradicting
  // candidate's locality ghosts — see the gate below.
  // Deliberately NOT gated on `holdTypedValues`. Hold mode only defers the
  // commit-time standardization flash (e.g. "highway" → "hwy") to the explicit
  // Save — it must not disable the live soft-overwrite. A ghost is a preview,
  // not a committed rewrite: the field reverts if the user leaves it alone, and
  // at Save the consumer captures the autofill delta separately (see
  // AddressInput's `savingRef` / `pendingSaveDeltaRef`) so no canonical value
  // ever flashes into a still-mounted input. So a stale field gets ghosted in
  // hold mode the same as anywhere else; tabbing then accepts each field's own
  // ghost in turn (onFieldBlur explicit path).
  // `autoVerificationOff` DOES keep it off: the breaker trips when the user
  // undoes a standardization, i.e. takes control of the value. Ghosting their
  // deliberate override would re-overwrite what they just reclaimed — this gate
  // is what makes "use what I typed" stick (the breaker also closes `canFetch`,
  // so no candidate arrives to ghost with).
  const { resetStash: resetSubfieldStash, userEditedRef: editedSubfieldsRef } =
    useStaleSubfieldGhosting({
      value,
      topCandidate,
      active: enabled && !isVerified && !isSkipped && !autoVerificationOff,
      onChange,
      isConsistent: valueConsistent,
    });

  const shadows = useMemo(() => {
    // `autoVerificationOff` (the breaker) gates every COMMIT path; gate the
    // ghost on it too, so display and commit can't diverge. Without this, the
    // ghost is held off only incidentally — by the `validationSkipped="1"` the
    // breaker stamps (→ isSkipped). A surface that clears validationSkipped on
    // edit (FamilyRegistration / the giving donor address) drops that marker on
    // the next subfield edit and un-masks a ghost from the still-cached
    // candidate — a "dead" ghost no gesture can commit while the breaker holds.
    if (isVerified || isSkipped || autoVerificationOff) return EMPTY_SHADES;
    // No matched candidate (no prefix match, or candidates contradict committed
    // fields): nothing to ghost. An ambiguous bare building does NOT land here —
    // its bare shell is dropped from the scoring pool so a unit group becomes
    // `topCandidate` (geo-best building, dominant designator), and its
    // placeholder ("…Ste ___") ghosts below.
    if (!topCandidate) return EMPTY_SHADES;
    // Locality ghost gate — display must match the COMMIT gate. The candidate
    // matched the typed STREET (the prefix filter), but `pickTopCandidate` only
    // SCORES on locality, so a candidate that CONTRADICTS a city/state/zip the
    // user TYPED still wins (e.g. they typed state IL, but the only Kirkwood
    // candidate is MO). Its locality is the wrong geography, so ghosting it into
    // an empty sibling — a MO zip under a typed IL — is misleading, and
    // `resolveOnGroupBlur` rejects the whole candidate as unverifiable anyway
    // (the `!acceptable` branch runs this SAME `candidateConsistentWithForm`).
    // Suppress the city/state/zip ghosts so the preview can't promise a fill the
    // commit won't keep. Only a TYPED contradiction suppresses (the gate ignores
    // a stale/untouched subfield — the clear-stash-restore replaces those). The
    // STREET ghost still completes the addr1 line independently — it matched what
    // the user typed; only the candidate's locality is in doubt.
    const localityContradicted = !candidateConsistentWithForm(
      topCandidate,
      value,
      editedSubfieldsRef.current
    );
    // Suppress the addr1 (unit) ghost in two cases; locality ghosts are
    // untouched:
    //   - `enteredUnitSlot`: the user has started typing their unit — kill the
    //     ghost so it can't mislead them into committing before the verification
    //     fetch lands (a lingering "___" used to do exactly that). The verdict
    //     still settles from the fetch; we just stop ghosting.
    //   - bare-building intent (Path 2 — the narrow breaker, sibling of
    //     base-retain): the user engaged the unit slot then deleted the label
    //     back to the bare street, so we don't re-nudge toward an abandoned unit.
    const suppressUnitGhost =
      enteredUnitSlot || (addressNeedsUnit(topCandidate) && bareBuildingIntent);
    // Don't extend a number the user is still typing ("PO Box 1" → "PO Box 10001").
    // The candidate stays alive so its locality keeps ghosting; only the street
    // number-extension is withheld — see extendsTrailingNumber + the commit gates.
    const suppressStreetGhost = extendsTrailingNumber(
      value.addr1 || "",
      topCandidate.addr1 || ""
    );
    return {
      addr1:
        suppressUnitGhost || suppressStreetGhost
          ? ""
          : deriveShadowSuffix(value.addr1 || "", topCandidate.addr1 || ""),
      city: localityContradicted
        ? ""
        : deriveShadowSuffix(value.city || "", topCandidate.city || ""),
      state: localityContradicted
        ? ""
        : deriveShadowSuffix(value.state || "", topCandidate.state || ""),
      zip: localityContradicted
        ? ""
        : deriveShadowSuffix(value.zip || "", topCandidate.zip || ""),
    };
  }, [
    topCandidate,
    isVerified,
    isSkipped,
    autoVerificationOff,
    enteredUnitSlot,
    bareBuildingIntent,
    value,
    editedSubfieldsRef,
  ]);

  // Freeze what the user is currently looking at — their own typed text plus
  // each shadow field's ghost suffix — into the value as plain text, with NO
  // verification metadata. This is the hold-mode Enter commit: it turns the gray
  // ghosts black while keeping the user's spelling ("8602 west grand ledge" +
  // " Hwy" → "8602 west grand ledge Hwy", not the abbreviated canonical "8602 W
  // Grand Ledge Hwy"). The standardized, verified canonical is the surface
  // Save's job; the difference between this frozen "seen" text and that
  // canonical is exactly what drives the standardization undo notice. Only
  // fields with a live ghost are written. Read through a ref by the keydown
  // listener so it always sees the latest shadows/value without re-binding.
  const commitVisibleGhostsRef = useRef(() => {});
  const commitVisibleGhosts = useCallback(() => {
    const delta = {};
    SHADOW_FIELDS.forEach((fieldName) => {
      const suffix = shadows[fieldName];
      if (!suffix) return;
      // Never freeze a multi-unit "…Apt ___" sentinel into the visible field —
      // the placeholder is a slot to fill, not a "seen" value. The keyboard
      // accept paths never call this with a placeholder-bearing addr1 ghost (the
      // multi-unit Enter branch handles the slot separately), but Save calls it
      // unconditionally, so guard here. Locality ghosts still freeze.
      if (fieldName === "addr1" && suffix.includes(UNIT_PLACEHOLDER)) return;
      delta[fieldName] = (value[fieldName] || "") + suffix;
    });
    if (Object.keys(delta).length) onChange(delta);
  }, [shadows, value, onChange]);
  commitVisibleGhostsRef.current = commitVisibleGhosts;

  // Freeze ONE field's visible ghost (its typed text + gray suffix) into the
  // value, no metadata — the per-field analog of commitVisibleGhosts. The
  // hold-mode Tab / ArrowRight accept commits ONLY the field the focus is
  // leaving (CLAUDE.md "Commit triggers"): tabbing out of the city must freeze
  // the city alone, never the zip the user hasn't reached. The locality each
  // freezes when its own field is left; Enter / group-blur are what commit all
  // fields at once. Read through a ref by the keydown listener.
  const commitVisibleGhostFieldRef = useRef(() => {});
  const commitVisibleGhostField = useCallback(
    (fieldName) => {
      const suffix = shadows[fieldName];
      if (!suffix) return;
      // Never freeze a multi-unit "…Apt ___" sentinel — it's a slot to fill.
      if (fieldName === "addr1" && suffix.includes(UNIT_PLACEHOLDER)) return;
      onChange({ [fieldName]: (value[fieldName] || "") + suffix });
    },
    [shadows, value, onChange]
  );
  commitVisibleGhostFieldRef.current = commitVisibleGhostField;

  // Hold flag, read at event time by the container focusout listener (whose
  // effect deps stay [containerRef], so it never re-binds on a prop change).
  const holdTypedValuesRef = useRef(holdTypedValues);
  holdTypedValuesRef.current = holdTypedValues;

  // Stable ref for shadows — the keydown effect reads it at event time via this
  // ref so that `shadows` can be removed from the effect's dep array. Without
  // this, the keydown listeners are torn down and re-attached on every keystroke
  // (because shadows updates each time addr1/city/state/zip change).
  const shadowsRef = useRef(shadows);
  shadowsRef.current = shadows;

  // Keep the debounce effect's "has the user typed a city?" signal in sync with
  // the stale-subfield hook's edited-set (the single source of truth for which
  // subfields the user touched). The ref is read in the debounce effect above;
  // syncing it here every render is safe because the effect only reads it inside
  // its debounce timeout, after this render has committed.
  cityEditedRef.current = Boolean(editedSubfieldsRef.current.city);

  // --- Per-field commit (blur / Enter / ArrowRight at end) ---------------

  const onFieldBlur = useCallback(
    (fieldName, explicit = false) => {
      // The breaker also silences the per-field commit: it canonicalizes from
      // `topCandidate` (still resolved from retained candidates), so without this
      // guard, blurring the street after a rejected standardization re-collapses
      // addr1 to the candidate the user rejected — independent of the group
      // resolve and even when a surface has cleared `validationSkipped`.
      if (isVerified || isSkipped || autoVerificationOff || !isUSCountry)
        return;
      // Smarty outage: don't per-field-canonicalize against a stale retained
      // candidate — keep the typed value untouched (see autocompleteUnavailable).
      if (autocompleteUnavailable) return;
      if (!topCandidate) return;
      if (!SHADOW_FIELDS.includes(fieldName)) return;
      // Hold mode: a plain blur — clicking to another address field, or the blur
      // that follows a Tab — FREEZES the leaving field's visible ghost (its typed
      // text + gray suffix, the user's own spelling), so a click-move commits the
      // same per-field result a keyboard move does. It must NOT write the
      // canonical in place (the mid-edit "West"→"W" flash) — that defers to the
      // explicit Save; freezing the ghost the user already saw is not that flash.
      // The state SELECT's accept (onShadowCommit, explicit=true) skips this and
      // fills via the canonical path below — its candidate value IS the 2-letter
      // code, so there's no spelling to preserve.
      if (holdTypedValues && !explicit) {
        commitVisibleGhostFieldRef.current(fieldName);
        return;
      }
      // Multi-unit addr1: don't per-field-commit the placeholder. The
      // group-blur autofill path is the one that brings in the
      // placeholder (along with city/state/zip + slot focus) as a unit.
      // If we committed addr1 alone here, the slot-select effect would
      // refocus addr1 — which cancels the group-blur detector, so the
      // rest of the address never lands.
      if (
        fieldName === "addr1" &&
        typeof topCandidate.entries === "number" &&
        topCandidate.entries > 1
      ) {
        return;
      }
      // Base-retain per-field commit: the user typed a street Smarty recognized
      // plus an appended unit it can't complete ("…Road apt 999"), and is now
      // Tabbing OUT OF THE STREET to a sibling field. The forward `matches` check
      // below bails on this (the typed value extends PAST the candidate base, so
      // it isn't a prefix), which used to leave a per-field Tab uncanonicalized —
      // only a full group-blur standardized the base. A forward-completed street
      // DOES standardize on that same Tab, so this mirrors the group-blur branch
      // here for parity: canonicalize the recognized base in place ("Road" → "Rd")
      // with the unit kept verbatim, and surface the standardization undo.
      //
      // Defer while a fetch is in flight — the appended unit may still resolve to
      // a real Smarty leaf (verify-on-commit timing), so let the group-blur / leaf
      // re-fire decide rather than settle base-retain early. Inline (non-hold)
      // only: hold surfaces standardize at their explicit Save (the
      // `holdTypedValues && !explicit` guard above already returned a plain blur).
      if (fieldName === "addr1" && appendedTail && !holdTypedValues) {
        if (fetchInFlight) return;
        const { fill, streetStandardized, undoPayload } = buildBaseRetainCommit(
          value.addr1 || "",
          topCandidate,
          appendedTail,
          value,
          editedSubfieldsRef.current
        );
        // Capture the raw typed value BEFORE this commit canonicalizes addr1, so
        // the eventual group-blur base-retain re-fire compares against what the
        // user actually typed (not the canonical we just wrote) and so doesn't
        // clear this standardization. Only the first commit in a group-leave
        // captures; the group resolve consumes it.
        if (!seenBeforeCommitRef.current)
          seenBeforeCommitRef.current = snapshotCurrentSubfields(value);
        resetSubfieldStash(fill);
        setVerificationBaseline(null);
        canonicalizedStreetRef.current = null;
        // Mark the write as the hook's own so the subfield-change effect doesn't
        // read the canonicalized addr1 / filled locality as a user edit and wipe
        // the verdict it's about to set.
        baseRetainWriteRef.current = true;
        onChange(isSkipped ? fill : { ...fill, validationSkipped: "1" });
        setIsUnverifiable(true);
        // Standardization-undo only when canonicalizing changed the typed STREET
        // tokens (never merely because locality filled), matching the group-blur
        // base-retain branch.
        if (streetStandardized) setStandardization(undoPayload);
        else setStandardization(null);
        return;
      }
      const candidateValue = topCandidate[fieldName] || "";
      const currentValue = value[fieldName] || "";
      if (!candidateValue) return;
      // addr1 may differ by USPS abbreviation expansion ("West" vs "W"),
      // so use the token-aware match. Other fields stay on literal prefix.
      // A bare number-extension ("PO Box 1" → "PO Box 10001") is not a commit —
      // Tabbing out must not canonicalize the street to a box the user didn't
      // choose (mirrors the group-blur `acceptable` gate).
      const matches =
        fieldName === "addr1"
          ? abbreviationAwarePrefixMatch(currentValue, candidateValue) &&
            !extendsTrailingNumber(currentValue, candidateValue)
          : candidateValue.toLowerCase().startsWith(currentValue.toLowerCase());
      if (!matches) return;
      const delta = {};
      if (candidateValue !== currentValue) delta[fieldName] = candidateValue;
      // Stale city/state/zip that disagree with the candidate are cleared
      // live by the clear-stash-restore effect as soon as the candidate
      // arrives — well before this blur — so there's nothing to clear here.
      // (A ghost is a suffix of the input's existing text; the effect empties
      // the field so the candidate can render as a full-field ghost, and
      // stashes the original for restore.)
      if (Object.keys(delta).length) {
        // Capture the raw typed values BEFORE this commit canonicalizes the
        // field, so a later group resolve can detect (and offer to undo) a
        // standardization the ghost never previewed. Only the first commit in a
        // group-leave sequence captures; a resolve clears it.
        if (
          delta[fieldName] &&
          currentValue &&
          delta[fieldName] !== currentValue &&
          !seenBeforeCommitRef.current
        ) {
          seenBeforeCommitRef.current = snapshotCurrentSubfields(value);
        }
        onChange(delta);
        // Inline accept of the STREET ghost is a per-field commit, but it can
        // apply a street standardization the user never saw in the ghost ("8602
        // west grand le" + ghost "dge Hwy" → "8602 W Grand Ledge Hwy", with
        // "west"→"W"). Fire the SAME treatment the group-blur resolve runs, so the
        // inline-accept path isn't a blind spot: arm the breaker's reject guard
        // (`canonicalizedStreetRef`, unit-free) so editing the canonical back stops
        // autocomplete, and — when the engine rewrote a street token the user
        // didn't see — surface the undo callout. Fires on ANY inline street commit
        // on a NON-hold surface — Tab / ArrowRight AND a focus-move to another
        // field — since all standardize the street the same way (gating to
        // explicit-only left a focus-move-then-edit-back blind spot). Hold surfaces
        // are excluded (they standardize at Save). On a group-leave this may also
        // run in the group-blur resolve, but the callout + ref-arm are idempotent,
        // and this branch only reaches here on a real FORWARD commit — the
        // base-retain (appended-unit) case is handled by its own commit above, and
        // multi-unit / no-match bail earlier — so it can't surface a callout the
        // resolve wouldn't.
        if (fieldName === "addr1" && !holdTypedValues) {
          if (topCandidate.streetLine)
            canonicalizedStreetRef.current = topCandidate.streetLine;
          const seen = snapshotCurrentSubfields(value);
          // The undo baseline must carry the candidate's autofilled locality the
          // user SAW ghost in — not just the committed street delta. On this
          // per-field accept the locality was only ever a ghost (the user hasn't
          // tabbed through those fields), so `value` has it empty; undoing now
          // must still keep it (the user "saw" it fill). Build the baseline from
          // the full candidate, mirroring the group-blur autofill, but PRESERVE a
          // city/state/zip the user TYPED that contradicts the candidate (the
          // candidateConsistentWithForm rule).
          const saved = {
            ...value,
            ...candidateLocalityFill(
              topCandidate,
              value,
              editedSubfieldsRef.current
            ),
            ...delta,
          };
          if (savedDiffersFromSeen(seen, saved))
            setStandardization(buildUndoPayload(seen, saved));
        }
      }
    },
    [
      isVerified,
      isSkipped,
      autoVerificationOff,
      isUSCountry,
      autocompleteUnavailable,
      topCandidate,
      appendedTail,
      fetchInFlight,
      value,
      onChange,
      resetSubfieldStash,
      holdTypedValues,
      editedSubfieldsRef,
    ]
  );

  // Stable ref for onFieldBlur — the keydown effect reads it at event time via
  // this ref so that `onFieldBlur` can be removed from the effect's dep array.
  // Without this, the listeners re-attach whenever onFieldBlur rehashes (it
  // closes over `value`, which changes every keystroke).
  const onFieldBlurRef = useRef(onFieldBlur);
  onFieldBlurRef.current = onFieldBlur;

  // --- Keyboard accept on text fields: Enter / ArrowRight-at-end ---------

  const fieldRefs = useMemo(
    () => ({ addr1: addr1Ref, city: cityRef, zip: zipRef }),
    [addr1Ref, cityRef, zipRef]
  );

  useEffect(() => {
    const cleanups = [];
    KEYDOWN_FIELDS.forEach((fieldName) => {
      const el = fieldRefs[fieldName] && fieldRefs[fieldName].current;
      if (!el) return;
      const handleShadowKeydown = (e) => {
        // Repeat Enter on a committed-but-unfilled unit slot: the user accepted
        // "…Unit ___" but pressed Enter again without typing a number. There's
        // no shadow on a committed slot, so this sits ABOVE the shadow gate.
        // Blur the group — the focusout-driven resolveOnGroupBlur then strips
        // the sentinel and settles the address unverified. preventDefault keeps
        // this Enter from submitting a surrounding form with the "___" sentinel
        // still in the field. Hold surfaces (AddressInput, cell popover) drive
        // the same resolution through their own Save-on-Enter (onCommitEnter /
        // implicit submit), so skip them here to avoid double-handling.
        if (
          e.key === "Enter" &&
          !holdTypedValues &&
          fieldName === "addr1" &&
          el.value.includes(UNIT_PLACEHOLDER)
        ) {
          e.preventDefault();
          el.blur();
          return;
        }
        // Non-hold Enter with NO shadow but an acceptable top candidate: the
        // typed addr1 already exactly equals a Smarty leaf (a filled unit slot,
        // or a plain exact match), so the ghost suffix collapsed to "" — yet
        // the candidate is real and verifiable. Commit + verify it via
        // resolveOnGroupBlur(false, true) rather than bailing on the shadow gate
        // below (a group blur reaches the same accept path through the exact
        // retained-candidate match). This is the
        // same accept path the shadow-present non-hold Enter takes further down
        // — just reached when there's nothing left to ghost. Scoped to addr1
        // (the field carrying the street match) and to non-hold surfaces: hold
        // surfaces drive their own Save-on-Enter (onCommitEnter / implicit
        // submit), so routing here would double-handle and flash a canonical
        // into an input that's one frame from unmounting. Requires a
        // topCandidate — with none, Enter falls through to the surface (there's
        // nothing address-specific to commit; the group-blur resolve settles
        // the unverified verdict when the user actually leaves).
        if (
          e.key === "Enter" &&
          !holdTypedValues &&
          fieldName === "addr1" &&
          !shadowsRef.current.addr1 &&
          topCandidateRef.current
        ) {
          e.preventDefault();
          resolveOnGroupBlurRef.current(false, true);
          el.blur();
          return;
        }
        // Hold-surface counterpart of the branch above. A multi-unit entry
        // suppresses the addr1 (unit) ghost (`enteredUnitSlot`), but the locality
        // ghosts (city/state/zip) are still live. Enter must STILL freeze those
        // visible ghosts to black and consume the keystroke so the surface's
        // onCommitEnter drives Save — just like a plain hold-mode Enter does for a
        // fully-ghosted candidate further down. Without this, Enter returned inert
        // at the shadow gate below: the locality ghosts never turned black and Save
        // fell through to the browser's implicit form submit alone. Scoped to the
        // empty-addr1-ghost case (the placeholder "___" ghost is handled by the
        // multi-unit branch below) and to a real topCandidate (a no-completion /
        // did-you-mean address keeps falling through so a surrounding form's
        // Enter-to-submit still works).
        if (
          e.key === "Enter" &&
          holdTypedValues &&
          fieldName === "addr1" &&
          !shadowsRef.current.addr1 &&
          topCandidateRef.current
        ) {
          e.preventDefault();
          commitVisibleGhostsRef.current();
          return;
        }
        if (!shadowsRef.current[fieldName]) return;
        // Tab on addr1 while a multi-unit ghost is showing: commit only
        // the addr1 ghost (extending to "...Apt ___") and keep focus on
        // addr1 with the placeholder selected. Don't fall through to
        // group-blur resolution — the user isn't leaving the address
        // card, they're still filling the unit. Touching city/state/zip
        // here would be presumptuous, and routing through resolve
        // would settle an unverified verdict on non-singleton results.
        if (
          e.key === "Tab" &&
          !e.shiftKey &&
          fieldName === "addr1" &&
          shadowsRef.current.addr1.includes(UNIT_PLACEHOLDER)
        ) {
          const candidateAddr1 =
            (topCandidateRef.current && topCandidateRef.current.addr1) || "";
          if (candidateAddr1.includes(UNIT_PLACEHOLDER)) {
            e.preventDefault();
            pendingSlotSelectRef.current = true;
            // The user engaged the unit slot — remember it so a later
            // delete-back-to-bare-street is recognized as Path-2 bare-building
            // intent (and we don't re-nudge them toward a unit).
            slotEngagedRef.current = true;
            // Hold surfaces keep the user's TYPED street spelling with the
            // placeholder slot appended (the suffix the user is looking at —
            // `shadows.addr1`, e.g. " Ste ___"), NOT the canonical
            // `candidateAddr1` ("233 S Wacker Dr Ste ___"). Canonicalization
            // defers to Save (the standardized POST), the multi-unit analog of
            // the hold-mode Tab/ArrowRight rule that no longer rewrites the
            // typed street in place. Deriving the slot from `shadows.addr1`
            // means the painted value can't disagree with the ghost. Non-hold
            // surfaces still paint the canonical.
            const heldAddr1 =
              (valueRef.current.addr1 || "") + shadowsRef.current.addr1;
            onChangeRef.current(
              writeMeta({ addr1: holdTypedValues ? heldAddr1 : candidateAddr1 })
            );
          }
          return;
        }
        if (e.key === "Tab" && !e.shiftKey) {
          // Hold mode: Tab FREEZES the LEAVING field's visible ghost — the
          // candidate value the user is looking at in THIS field (its typed text
          // + gray suffix) turns into real black text, keeping their own spelling
          // ("8602 west grand" + " Ledge Hwy"), never the abbreviated canonical
          // (that rides the explicit Save). Only the leaving field commits, NOT
          // the whole candidate: tabbing out of the city must not also fill the
          // zip the user hasn't reached — each field freezes when its own focus
          // leaves. Return WITHOUT preventDefault so focus advances normally; the
          // resulting plain blur hits the `holdTypedValues && !explicit` guard in
          // onFieldBlur and no-ops (the freeze already happened here). The state
          // SELECT keeps its own explicit accept — see that guard.
          if (holdTypedValues) {
            commitVisibleGhostFieldRef.current(fieldName);
            return;
          }
          // Non-hold: Tab is an explicit single-field accept of THIS field's own
          // ghost — it commits the field's completion, then lets focus advance
          // normally (no preventDefault). It deliberately does NOT pull the
          // candidate's other subfields in — cross-field standardization still
          // defers to the explicit Save. Tabbing through the address commits each
          // field's own ghost in turn, never flashing a canonical value into a
          // field the user hasn't left yet. A plain blur (click-out) stays
          // deferred; Shift+Tab is backward navigation — no commit.
          onFieldBlurRef.current(fieldName, true);
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          const multiUnitGhost =
            fieldName === "addr1" &&
            shadowsRef.current.addr1.includes(UNIT_PLACEHOLDER);
          // Hold-mode Enter (AddressInput, the cell popover), non-multi-unit:
          // freeze EXACTLY what the user is looking at — their own typed text
          // plus each field's gray ghost suffix — into the inputs as real black
          // text, WITHOUT standardizing or verifying. "8602 west grand ledge" +
          // ghost " Hwy" becomes "8602 west grand ledge Hwy", keeping the user's
          // spelling rather than swapping in the abbreviated canonical "8602 W
          // Grand Ledge Hwy". The surface catches this prevented Enter to drive
          // its Save, which performs the real accept: it POSTs the standardized,
          // verified candidate and — because the saved canonical differs from
          // this frozen "seen" text — surfaces the undo notice. (Hold mode only
          // ever defers the *surprise* mid-edit standardization; freezing the
          // visible ghost the user is accepting is no surprise.) Keep focus; the
          // surface owns Save + focus.
          if (holdTypedValues && !multiUnitGhost) {
            commitVisibleGhostsRef.current();
            return;
          }
          // Multi-unit ghost: paint the "…Apt ___" slot + city/state/zip and let
          // the slot-select effect highlight the "___", then STOP. stopPropagation
          // keeps this Enter from also reaching the surface's Enter→Save handler
          // (onCommitEnter / implicit submit): that would run a SECOND resolve a
          // beat later — after the slot flushed into the value — which the
          // committed-slot branch would read as "user left an unfilled slot" and
          // strip + settle unverified. The slot must just sit there for the user
          // to fill; that resolution is the NEXT leave's job.
          if (multiUnitGhost) {
            e.stopPropagation();
            resolveOnGroupBlurRef.current(false, true);
            return; // stay in addr1 to fill the slot
          }
          // Non-hold surfaces: accept the WHOLE top candidate (every subfield +
          // verification metadata) via the same `resolveOnGroupBlur(_, true)`
          // path a Save click takes.
          resolveOnGroupBlurRef.current(false, true);
          // Non-hold surfaces: the address is accepted and verified — drop focus
          // off the field so the user isn't left parked in the input. The
          // verified state makes the resulting group-blur resolve a no-op. The
          // trailing
          // per-field blur-commit is harmless: every consumer now merges each
          // partial patch against the latest committed value (the wrappers route
          // through the famRegStore; see UserAddressField/HouseholdAddressField),
          // so an addr1-only write can no longer clobber the filled
          // city/state/zip.
          el.blur();
          return;
        }
        if (e.key === "ArrowRight") {
          const atEnd =
            el.selectionStart === el.value.length &&
            el.selectionEnd === el.value.length;
          // ArrowRight-at-end accepts the forward ghost — the sibling of the Tab
          // rule above. Hold mode FREEZES the leaving field's visible ghost
          // (typed text + its suffix, the user's own spelling); non-hold accepts
          // the canonical completion in place. Either way it's a per-field accept
          // (only this field), and the canonical standardization on a hold
          // surface still defers to the explicit Save.
          if (atEnd) {
            e.preventDefault();
            if (holdTypedValues) {
              commitVisibleGhostFieldRef.current(fieldName);
            } else {
              onFieldBlurRef.current(fieldName, true);
            }
          }
        }
      };
      el.addEventListener("keydown", handleShadowKeydown);
      cleanups.push(() =>
        el.removeEventListener("keydown", handleShadowKeydown)
      );
    });
    return () => cleanups.forEach((cleanup) => cleanup());
    // `shadows` is in the dep array so the listeners re-attach when the editing
    // inputs first mount (the inputs mount asynchronously via FnrSlides/
    // AnimatePresence; addr1Ref.current is null on the enabled-change render, but
    // populated by the time the first shadow arrives). All other handler reads
    // go through refs (onFieldBlurRef, resolveOnGroupBlurRef, shadowsRef, etc.)
    // so they don't need to appear here.
  }, [fieldRefs, holdTypedValues, shadows]);

  // --- Resolve verification when the user leaves the address group -------
  // On group-blur we accept the candidate that matches what the user typed
  // (`topCandidate`) — every subfield is overwritten with its value and
  // verification metadata is written. "Matches" means two things: the typed
  // street is an (abbreviation-aware) prefix of the candidate, AND the
  // candidate doesn't contradict a city/state/zip the user committed. Anything
  // with no acceptable candidate resolves to Unverifiable (the badge surfaces
  // the unverified state): candidates came back but NONE completes the typed
  // street (`!topCandidate`, no shadow rendered), a candidate completes the
  // street but contradicts a committed field (a shadow still renders, the typed
  // values are left standing), or zero candidates. See the `!acceptable` branch
  // below.

  // Returns a verdict object describing what just happened so the caller
  // can branch on it synchronously without waiting for React to commit.
  // Verdict shape: { kind: "noop"|"deferred"|"unverifiable"|"autofilled" }.
  // An "unverifiable" verdict may additionally carry `baseRetain` (appended-unit
  // canonicalization) OR `bareBuilding` ({ fill, streetStandardized, undoPayload }
  // — a multi-unit building with no unit) so hold surfaces POST the same
  // canonical address + standardization decision the inline surfaces commit.
  //
  // `acceptTopCandidate` — set by Save-click callers: treat `topCandidate`
  // as the user's pick and autofill rather than leave it unverified (the shadow
  // text was already offering it, so the click reads as acceptance). It also
  // bypasses the consistency gate — an explicit Save is the user owning the
  // overwrite. Auto group-blur callers leave it false, so a contradiction stays
  // Unverifiable (typed values untouched) instead of being silently accepted.
  // `forceSettle` — skip the in-flight defer gate and settle against whatever is
  // in hand right now (used by `awaitVerificationVerdict` after its poll window
  // expires with the fetch still running: better to POST the typed address
  // unverified than hang the Save). With no candidate it falls through to the
  // `!acceptable` branch → unverifiable.
  const resolveOnGroupBlur = useCallback(
    (isExplicit = false, acceptTopCandidate = false, forceSettle = false) => {
      if (!enabled) return { kind: "noop" };
      if (!addressIsDirty) return { kind: "noop" };
      if (isVerified) return { kind: "noop" };
      // The breaker (a rejected standardization — clicking undo, or a manual
      // edit that alters the standardized street) turns the WHOLE flow off,
      // including this resolve. `canFetch` already blocks new fetches, but a
      // retained candidate still resolves `topCandidate`, so without this guard
      // a stray group-blur re-collapses to the very candidate the user rejected
      // (re-applying the standardization with no network call). It must hold
      // even when a surface has cleared `validationSkipped` on edit (so
      // `skipBlocksFlow` is false — see FamilyRegistration's AddressProfileField)
      // and even on an explicit Save: the rejected canonical must never return.
      if (autoVerificationOff) return { kind: "noop" };
      // Non-US: the flow is off, so a group-blur/Save must not commit any
      // verdict (no validationSkipped, no Unverifiable callout) — the address
      // behaves like a plain field, same as production. `canFetch` doesn't
      // reach this resolve, so the gate has to live here too.
      if (!isUSCountry) return { kind: "noop" };
      // Smarty outage: same OFF behavior as non-US — commit no verdict (no
      // validationSkipped, no Unverifiable callout) so the address behaves like
      // a plain field and the backend can still verify it on save. A retained
      // candidate could still resolve `topCandidate` here, so the gate must
      // live at this commit site, not only at `canFetch`.
      if (autocompleteUnavailable) return { kind: "noop" };
      // Once the address is settled-skipped, suppress only REDUNDANT autonomous
      // re-resolves (a stray group-blur). An explicit accept must still produce a
      // real verdict: a Save/Enter carries `acceptTopCandidate` (the Save path
      // passes isExplicit=false, acceptTopCandidate=true). Without the
      // `acceptTopCandidate` exception, tabbing from the last field to the Save
      // button — which fires an autonomous group-blur that stamps
      // validationSkipped FIRST — left the following Save resolving to a no-op,
      // so the unverifiable callout never appeared (Enter inside a field, which
      // runs no autonomous resolve first, did surface it).
      if (skipBlocksFlow && !isExplicit && !acceptTopCandidate)
        return { kind: "noop" };

      const candidate = topCandidate;

      // Fetch still in flight (or scheduled): defer. The candidates-effect
      // re-fires us once data lands so the decision sees real data. Carry
      // the explicit flag through so the re-fire bypasses the skip gate too.
      // `forceSettle` overrides this — the caller has waited long enough and
      // wants a verdict now, even if that verdict is unverifiable.
      if (fetchInFlight && !forceSettle) {
        // A clean forward candidate already in hand settles NOW, without paying
        // for the re-fetch to land — on a Save click AND on an autonomous
        // group-blur. Leaving the address group commits the focused street field
        // (`onFieldBlur`), whose canonical write kicks off a fresh fetch; but the
        // candidate that write came from is the same row that fetch will return,
        // so deferring only stalls the autofill — the city/state/zip ghosts sit
        // gray for the whole round-trip before turning into real text. The user
        // reads that delay as the ghost "lingering" after they accepted. Settle
        // against the in-hand candidate so the ghosts become real immediately.
        //
        // EXCEPT a base + appended-unit match (`appendedTail`): there the
        // candidate in hand is only the recognized BASE, and the typed unit is
        // unconfirmed — the in-flight fetch is querying the full string and may
        // return the unit as a verified leaf. Accepting the base-retain now would
        // settle a verifiable address as unverifiable whenever the user commits
        // before that fetch lands (the fast-Return bug: type a unit, hit Return
        // before the leaf arrives). So defer on an appended tail; likewise defer
        // when there's NO candidate yet (the fetch is still resolving the first
        // result). Both let the re-fire resolve against real data once it lands.
        if (!candidate || appendedTail) {
          pendingResolveRef.current = isExplicit ? "explicit" : "auto";
          return { kind: "deferred" };
        }
      }

      // Past the defer gate — this resolve terminates now. Consume the
      // keyboard-summon flag so only the open that follows THIS Enter
      // Committed-but-unfilled unit slot: the user accepted "…Unit ___" (the
      // sentinel is already in value.addr1) but is now leaving the group / hit
      // Enter again without typing a unit number. Never persist or re-autofill
      // the "___": strip it (and its trailing space) so the saved street is the
      // bare "…Unit", then settle the stripped address unverified. Every leave
      // routes through this resolve — group focusout (click-out / the keydown's
      // el.blur() on a non-hold repeat Enter) and explicit Saves (via
      // awaitVerificationVerdict) — so the sentinel can't reach a saved value by
      // any of them.
      if ((value.addr1 || "").includes(UNIT_PLACEHOLDER)) {
        const stripped = (value.addr1 || "")
          .split(UNIT_PLACEHOLDER)
          .join("")
          .replace(/\s+$/, "");
        setVerificationBaseline(null);
        canonicalizedStreetRef.current = null;
        resetSubfieldStash({ addr1: stripped });
        // Mark the strip as the hook's own write so the subfield-change effect
        // doesn't read the addr1 change as a user edit and clear the verdict.
        slotStripWriteRef.current = true;
        onChange(
          isSkipped
            ? { addr1: stripped }
            : { addr1: stripped, validationSkipped: "1" }
        );
        setIsUnverifiable(true);
        seenBeforeCommitRef.current = null;
        return { kind: "unverifiable" };
      }

      // Base + appended unit: the user typed past a street Smarty recognized
      // (e.g. "…Road Apt 4") and Smarty has no record of the unit. Canonicalize
      // the recognized base to the candidate's exact formatting ("Road" → "Rd",
      // casing, directionals), re-append the user's unit/tail verbatim, and
      // commit that as addr1 along with the base's city/state/zip. The verdict
      // still settles unverifiable: the appended tail isn't a Smarty leaf, so we
      // must not tell the backend it's verified. Hold surfaces don't autofill
      // here — their own Save commit handler runs the same shared helper to
      // build the saved payload at the reveal.
      if (candidate && appendedTail) {
        if (holdTypedValues && !acceptTopCandidate) return { kind: "noop" };
        // Prefer the raw typed value a prior per-field blur captured: a Tab out
        // of the street already canonicalized addr1, so reading the current addr1
        // here would compare the canonical against itself (streetStandardized
        // false) and clear the callout the per-field commit set. The captured
        // snapshot is the user's original spelling, so the standardization
        // decision and the undo payload stay keyed to what they typed.
        const typedForBaseRetain = seenBeforeCommitRef.current?.addr1 || addr1;
        const { fill, streetStandardized, undoPayload } = buildBaseRetainCommit(
          typedForBaseRetain,
          candidate,
          appendedTail,
          value,
          editedSubfieldsRef.current
        );
        resetSubfieldStash(fill);
        setVerificationBaseline(null);
        canonicalizedStreetRef.current = null;
        // Mark the write as the hook's own so the subfield-change effect doesn't
        // read the canonicalized addr1 / filled locality as a user edit and wipe
        // the verdict it's about to set.
        baseRetainWriteRef.current = true;
        onChange(isSkipped ? fill : { ...fill, validationSkipped: "1" });
        setIsUnverifiable(true);
        seenBeforeCommitRef.current = null;
        // BOTH callouts when both are true (§2). The standardization-undo fires
        // ONLY when canonicalizing changed the typed STREET tokens — never
        // merely because locality auto-filled. Its undo restores the full
        // original typed string (street + unit verbatim).
        if (!holdTypedValues && streetStandardized) {
          setStandardization(undoPayload);
        } else {
          setStandardization(null);
        }
        // Carry the base-retain decision on the verdict so hold/freeze-then-
        // reveal surfaces (admin, reports) make the SAME street-keyed
        // standardization call at their Save instead of re-diffing the whole
        // value (which mis-fires on auto-filled locality). `streetStandardized`
        // is the only standardization signal; `undoPayload` restores the full
        // typed string.
        return {
          kind: "unverifiable",
          baseRetain: { streetStandardized, undoPayload },
        };
      }

      // Bare-building intent (Path 2 — the narrow breaker): a multi-unit building
      // whose bare street the user typed in full, with no unit, AFTER tripping the
      // breaker by engaging the unit slot ("…Apt ___") and then deleting the label
      // back down to the bare street. Commit the bare building: canonical street
      // (candidate.streetLine), no secondary, locality from the candidate,
      // settling unverifiable. A bare multi-unit building is not a Smarty leaf, so
      // we must not mark it verified. This is the inverse of base-retain (there
      // the user APPENDS a tail; here they STOP at the bare street).
      //
      // Gate STRICTLY on `bareBuildingIntent` — that is the explicit user signal,
      // and while it's set the unit ghost + Add-unit button are both suppressed,
      // so Enter and blur should BOTH commit the bare building. There is no
      // "type the full street and blur auto-commits" path: a merely-complete bare
      // street still ghosts the unit and runs the normal multi-unit flow. Because the intent flag itself can only be set
      // when the slot is abandoned, the explicit unit-fill gestures (Add-unit
      // button, Enter on the ghost) can never reach this branch — they only fire
      // while intent is false. So `!acceptTopCandidate` is NOT needed here; a
      // hold-surface Save commits the bare building too. The placeholder-strip
      // branch above already handled a sentinel in value.addr1, so here
      // value.addr1 is sentinel-free.
      if (
        bareBuildingIntentRef.current &&
        candidate &&
        addressNeedsUnit(candidate) &&
        addr1Equivalent(value.addr1 || "", candidate.streetLine || "")
      ) {
        // Autonomous group-blur on a hold surface: don't autofill — leave the
        // typed values standing (the surface owns Save). Mirrors base-retain.
        if (holdTypedValues && !acceptTopCandidate) return { kind: "noop" };
        const { fill, streetStandardized, undoPayload } =
          buildBareBuildingCommit(
            candidate,
            addr1,
            value,
            editedSubfieldsRef.current
          );
        resetSubfieldStash(fill);
        setVerificationBaseline(null);
        canonicalizedStreetRef.current = null;
        // Mark the write as the hook's own so the subfield-change effect doesn't
        // read the canonicalized addr1 / filled locality as a user edit and wipe
        // the verdict it's about to set. (On a hold-surface Save the surface's
        // savingRef captures this delta for the POST instead of writing it.)
        bareBuildingWriteRef.current = true;
        onChange(isSkipped ? fill : { ...fill, validationSkipped: "1" });
        setIsUnverifiable(true);
        seenBeforeCommitRef.current = null;
        // BOTH callouts when both are true (§2). The standardization-undo fires
        // ONLY when canonicalizing changed the typed STREET tokens (e.g.
        // "…Place" → "…Pl") — never merely because locality auto-filled. Its undo
        // restores the user's original typed street.
        if (!holdTypedValues && streetStandardized) {
          setStandardization(undoPayload);
        } else {
          setStandardization(null);
        }
        // Carry the bare-building decision on the verdict so hold/freeze-then-
        // reveal surfaces (admin, reports) make the SAME street-keyed
        // standardization call at their Save. `fill` is carried so a surface that
        // can't capture the onChange autofill can still POST the same canonical
        // address. Mirrors base-retain's verdict.baseRetain.
        return {
          kind: "unverifiable",
          bareBuilding: { fill, streetStandardized, undoPayload },
        };
      }

      // Accept the matched candidate when it completes the typed street AND
      // doesn't contradict a subfield the user TYPED. The consistency gate runs
      // on EVERY commit path — a Save-click is explicit acceptance of the
      // *offered* candidate, not a license to overwrite a locality the user
      // typed. A divergent touched subfield (e.g. a mailable alternate city the
      // candidate disagrees with) keeps the typed value and settles unverifiable
      // via the `!acceptable` branch below, exactly like Enter/tab. A *stale*
      // (untouched) subfield never blocks — the gate ignores it so the candidate
      // standardizes the address (see `editedSubfieldsRef` plumbing).
      // A candidate that only extends the number the user is still typing
      // ("PO Box 1" → "PO Box 10001") is not an acceptable completion of the
      // street: committing it would silently rewrite + verify a box number the
      // user never chose. Fall through to the Unverifiable landing, leaving the
      // typed street standing (the same treatment as a contradiction).
      const acceptable =
        candidate &&
        !extendsTrailingNumber(value.addr1 || "", candidate.addr1 || "") &&
        candidateConsistentWithForm(
          candidate,
          value,
          editedSubfieldsRef.current
        );
      if (!acceptable) {
        // Nothing acceptable to commit lands here on the Unverifiable verdict.
        // Three shapes reach it, all the same outcome:
        //   - Did-you-mean: Smarty returned suggestions but NONE completes the
        //     typed street (`!candidate`, no shadow ever rendered) — e.g. typed
        //     "100 Main St", Smarty only knows "100 N/S Main St".
        //   - Contradiction: a candidate completes the street but disagrees with
        //     a committed city/state/zip. A shadow still renders; the typed
        //     values are left standing (this `!acceptable` gate is what prevents
        //     the silent overwrite), and on an explicit Save the
        //     StandardizationNotice surfaces the typed-vs-standardized diff with
        //     an undo. (A guided unit-fill never reaches here — its placeholder
        //     candidate IS a completion, so `acceptable` is true.)
        //   - Zero candidates (or a Save with nothing to accept).
        // validationSkipped="1" keeps the form gate passing; the user can retry
        // on demand or edit to re-enter silent-typing.
        if (!isSkipped) onChange({ validationSkipped: "1" });
        setIsUnverifiable(true);
        seenBeforeCommitRef.current = null;
        return { kind: "unverifiable" };
      }

      // Hold mode: an *autonomous* group-blur (focus left the card without a
      // Save click) must not autofill — that would flash the canonical values
      // into the still-visible inputs. Leave the typed values standing; the
      // explicit Save commit (acceptTopCandidate=true) is the one path that
      // standardizes. Inline accept / keyboard accept also pass acceptTopCandidate
      // / go through their own commit, so they're unaffected.
      if (holdTypedValues && !acceptTopCandidate) return { kind: "noop" };

      // What the user saw before this collapse: the snapshot captured by the
      // first per-field commit if a blur already canonicalized a field, else the
      // live value (Enter resolves before any blur, so it's still the typed
      // text). Consumed here.
      const typedSnapshot =
        seenBeforeCommitRef.current || snapshotCurrentSubfields(value);
      seenBeforeCommitRef.current = null;
      const filled = {};
      ADDRESS_SUBFIELDS.forEach((fieldName) => {
        const candidateFieldValue = candidate[fieldName];
        if (candidateFieldValue !== undefined && candidateFieldValue !== "")
          filled[fieldName] = candidateFieldValue;
      });
      if (
        typeof filled.addr1 === "string" &&
        filled.addr1.includes(UNIT_PLACEHOLDER)
      ) {
        pendingSlotSelectRef.current = true;
        // Painting the placeholder slot engages the unit — remember it so a
        // later delete-back-to-bare-street trips Path-2 bare-building intent.
        slotEngagedRef.current = true;
        // Hold surfaces keep the user's TYPED street spelling with the
        // placeholder slot appended (e.g. "233 s wacker drive Ste ___"), NOT
        // the canonical "233 S Wacker Dr Ste ___" — the multi-unit analog of
        // the hold-mode Tab rule that no longer rewrites the typed street in
        // place. Canonicalization defers to Save (the standardized POST). The
        // slot suffix is derived from the SAME source `shadows.addr1` uses
        // (deriveShadowSuffix against the candidate's placeholder'd addr1) so
        // the painted value and the ghost can't disagree. Locality still fills
        // from the candidate below — a freeze of ghosts the user already saw.
        // Non-hold keeps the canonical paint.
        if (holdTypedValues) {
          filled.addr1 =
            (value.addr1 || "") +
            deriveShadowSuffix(value.addr1 || "", candidate.addr1 || "");
        }
      }
      setVerificationBaseline(snapshotBaseline(value, filled));
      // Arm the reject discriminator: record the canonical, UNIT-FREE street
      // (`streetLine`) the engine just verified. We arm on EVERY in-session
      // verify (including a multi-unit slot paint), regardless of whether the
      // street was reformatted vs. typed verbatim — because "edit" is defined by
      // the VALUE diverging from this canonical, not by how the user entered it.
      // Typing the same canonical back keeps the prefix → not an edit → no trip;
      // changing a street word breaks the prefix → trip. Storing it unit-free
      // means correcting/appending a UNIT keeps the prefix → never trips. (Mount-
      // verified addresses never run this autofill, so editing them keeps helping.)
      // Not in hold mode: there the canonical is never the input's standing
      // value (freeze-then-reveal), so the typed value legitimately diverges from
      // the canonical and arming would false-trip the reject check. Hold surfaces
      // compute their own standardization/undo at Save.
      if (!holdTypedValues && candidate.streetLine) {
        canonicalizedStreetRef.current = candidate.streetLine;
      }
      // A subfield the user TYPED (e.g. "new" → accepted "New York") must stay
      // protected ACROSS this accept, not be demoted to a stale autofill.
      // `resetSubfieldStash` blanks the edit latches so the commit's own writes
      // aren't misread as edits — but a blanket reset also drops the city from
      // the Smarty query (`cityEditedRef`) and removes the strip-protection. So
      // when the street next changes (typing the unit), the re-fetch loses the
      // typed-city bias, reverts to the geo-default candidate, and the stale-
      // subfield ghosting strips the accepted locality. Re-arm the latches the
      // user had genuinely set so the accepted locality keeps biasing the query
      // and is never ghosted out from under them.
      const typedBefore = { ...editedSubfieldsRef.current };
      resetSubfieldStash(filled);
      Object.keys(typedBefore).forEach((fieldName) => {
        if (typedBefore[fieldName])
          editedSubfieldsRef.current[fieldName] = true;
      });
      onChange(writeMeta(filled));
      setIsUnverifiable(false);
      // Collapse-in-place standardization: on a mounted surface the canonical
      // we just wrote is now visible in the inputs. When it differs from what
      // the user *saw* (their typed text + the ghost we previewed) — e.g. a
      // USPS abbreviation applied to a fully-typed token ("highway" → "Hwy") —
      // offer the undo callout. Skip it on hold/freeze-then-reveal surfaces,
      // which never repaint the input and compute their own reveal-time
      // standardization at Save. A multi-unit slot is never a finished
      // standardization, so it carries no undo.
      if (
        !holdTypedValues &&
        !pendingSlotSelectRef.current &&
        savedDiffersFromSeen(typedSnapshot, { ...value, ...filled })
      ) {
        setStandardization(
          buildUndoPayload(typedSnapshot, { ...value, ...filled })
        );
      } else {
        setStandardization(null);
      }
      return { kind: "autofilled" };
    },
    [
      enabled,
      addressIsDirty,
      isVerified,
      isSkipped,
      skipBlocksFlow,
      autoVerificationOff,
      isUSCountry,
      autocompleteUnavailable,
      topCandidate,
      appendedTail,
      fetchInFlight,
      addr1,
      value,
      onChange,
      resetSubfieldStash,
      holdTypedValues,
      editedSubfieldsRef,
    ]
  );

  resolveOnGroupBlurRef.current = resolveOnGroupBlur;

  // Re-fire a deferred resolve once new candidates land. By the time
  // this effect runs, useMemo for topCandidate has recomputed against
  // the new candidate pool — so the resolve sees real data, not the
  // stale snapshot the .then() callback would have closed over.
  //
  // Keyed on `retainedCandidates` as well as `allCandidates`: the base-street
  // fallback lands into the retained pool while `allCandidates` stays empty (a
  // whole-string "<street> <bad unit>" never produces a primary result), so
  // without this dep the deferred base-retain resolve would wait forever for an
  // allCandidates change that never comes.
  useEffect(() => {
    if (pendingResolveRef.current) {
      const isExplicit = pendingResolveRef.current === "explicit";
      pendingResolveRef.current = null;
      resolveOnGroupBlurRef.current(isExplicit);
    }
  }, [allCandidates, retainedCandidates]);

  // --- Container focusout wiring -----------------------------------------

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const handleAddressGroupFocusOut = () => {
      // Defer so focus moving between sibling inputs doesn't fire the
      // group-blur. After the tick, if document.activeElement is still
      // inside the container, do nothing. Also skip when focus moved
      // into a portaled dropdown (listbox/dialog) — those are rendered
      // outside the container by design, but the user is still
      // interacting with one of the card's own fields.
      //
      // Read through the ref so the setTimeout callback sees the latest
      // resolve closure — load-bearing when a Save click runs in parallel:
      // handleSave's autofill commits isVerified=true and we want the
      // deferred resolve to see that and noop, not the pre-autofill state.
      setTimeout(() => {
        const node = containerRef.current;
        if (!node) return;
        const active = document.activeElement;
        if (node.contains(active)) return;
        if (active && active.closest('[role="listbox"], [role="dialog"]')) {
          return;
        }
        // Hold mode: leaving the address group commits the ENTIRE candidate the
        // user saw — freeze every visible ghost (their spelling, no metadata),
        // the click-out analog of pressing Enter, so a mouse exit fills the same
        // fields a keyboard exit does. The canonical/verdict still defer to Save
        // (resolveOnGroupBlur noops the autofill in hold). On a Save click this
        // is naturally a no-op: the Save handler's own commitVisibleGhosts has
        // already frozen the ghosts (shadows empty) before this deferred tick, so
        // it can't double-write into the in-flight save's captured delta.
        if (holdTypedValuesRef.current) commitVisibleGhostsRef.current();
        resolveOnGroupBlurRef.current();
      }, 0);
    };
    el.addEventListener("focusout", handleAddressGroupFocusOut);
    return () => el.removeEventListener("focusout", handleAddressGroupFocusOut);
    // `shadows` is in the deps for the SAME reason the keydown effect has it: on
    // surfaces whose editing inputs mount asynchronously (the admin profile's
    // FnrSlides editor), `containerRef.current` is still null when this effect
    // first runs, so binding only on `[containerRef]` would attach the listener
    // to nothing and the group-blur freeze (and resolveOnGroupBlur) would never
    // fire — only the per-field `onBlur` would. Re-running when the first shadow
    // arrives (by which point the container is mounted) attaches it. The handler
    // reads everything else through refs, so re-binding is otherwise harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, shadows]);

  // --- Editing a verified address clears the verified flag ---------------
  // Only on a *transition away from verified* triggered by user editing.
  // Pre-existing seeded verification on mount stays. We also distinguish
  // the hook's own internal writes (autofill / pick) from user edits via
  // the `prevVerifiedRef` snapshot — if isVerified just flipped to true
  // alongside the subfield change, the change is our own autofill and
  // must not be cleared.

  const prevSubfieldsRef = useRef([
    value.addr1 || "",
    value.city || "",
    value.state || "",
    value.zip || "",
    value.plus4 || "",
    value.country || "",
  ]);
  const prevVerifiedRef = useRef(isVerified);
  useEffect(() => {
    const prev = prevSubfieldsRef.current;
    const current = [
      value.addr1 || "",
      value.city || "",
      value.state || "",
      value.zip || "",
      value.plus4 || "",
      value.country || "",
    ];
    const changed = current.some(
      (currentValue, fieldIndex) =>
        fieldIndex !== PLUS4_SUBFIELD_INDEX && currentValue !== prev[fieldIndex]
    );
    const wasVerified = prevVerifiedRef.current;
    prevSubfieldsRef.current = current;
    prevVerifiedRef.current = isVerifiedRef.current;
    // A consumer reset (Cancel) reverted the fields programmatically. Re-seed
    // the verification baseline from the restored value and skip the edit/
    // reject rules below — they would otherwise read the revert as a user edit:
    // tripping the breaker (the canonical street the user just left is no longer
    // a prefix) or stripping the restored `isVerified` against a stale baseline
    // (which would leave Save stuck visible after Cancel). `reset()` has already
    // cleared the session flags; this restores the mount-time baseline invariant.
    if (resetPendingRef.current) {
      resetPendingRef.current = false;
      // Re-seed the stale-subfield bookkeeping to the restored locality.
      // Otherwise its own effect reads the reverted city/state/zip (e.g.
      // Sunfield → Boston) as user edits and latches them in `editedSubfieldsRef`
      // — which then suppresses the clear-for-ghost on the next edit, so the
      // locality ghosts vanish on a re-typed street.
      resetSubfieldStash({
        city: value.city,
        state: value.state,
        zip: value.zip,
      });
      setVerificationBaseline(
        isVerifiedRef.current ? snapshotCurrentSubfields(value) : null
      );
      return;
    }
    if (!changed) return;
    const currentAddr1 = current[0] || "";
    // The hook just stripped a committed unit sentinel and settled the address
    // unverified (resolveOnGroupBlur). That addr1 write is our own, not a user
    // edit — process it as a no-op so the verdict-clearing rules below don't
    // wipe the validationSkipped="1" / isUnverifiable the strip just set.
    if (slotStripWriteRef.current) {
      slotStripWriteRef.current = false;
      return;
    }
    // The hook's own base-retain canonicalization write (addr1 + locality,
    // settling unverifiable). Not a user edit — leave the verdict and the
    // standardization callout the commit just set intact.
    if (baseRetainWriteRef.current) {
      baseRetainWriteRef.current = false;
      return;
    }
    // The hook's own bare-building commit write (canonical bare street +
    // locality, settling unverifiable). Not a user edit — leave the verdict and
    // the standardization callout the commit just set intact. Mirrors
    // baseRetainWriteRef.
    if (bareBuildingWriteRef.current) {
      bareBuildingWriteRef.current = false;
      return;
    }
    // Bare-building intent tracking (Path 2). Independent of the verdict-clearing
    // rules below — it only flips a live-suppression flag, so it never returns.
    const latestTopCandidate = topCandidateRef.current;
    if (bareBuildingIntentRef.current) {
      // Reset on a NEW building: the multi-unit candidate changed to a different
      // street_line while intent was set, so the old "no unit here" decision no
      // longer applies. Gate on building IDENTITY (a different streetLine), NOT
      // on addr1 drifting past the bare street — typing a unit after the trip
      // ("…Apt 4", addr1 ≠ streetLine but SAME building) must NOT reset, or the
      // ghost/Add-unit nudge would snap back mid-correction. With no multi-unit
      // candidate at all (street emptied toward a different building) the prior
      // intent is likewise stale.
      const sameBuilding =
        latestTopCandidate &&
        addressNeedsUnit(latestTopCandidate) &&
        latestTopCandidate.streetLine &&
        // addr1 is the bare street OR an extension of it (a unit being typed) —
        // either way it's still THIS building.
        abbreviationAwarePrefixMatch(
          latestTopCandidate.streetLine,
          currentAddr1
        );
      if (!sameBuilding) {
        setBareBuildingIntent(false);
        slotEngagedRef.current = false;
      }
    } else if (
      // Trip Path 2: the user engaged a multi-unit slot ("…Apt ___") and has now
      // deleted addr1 back down to the bare building street (no designator left,
      // no "___"). Trip the breaker so the unit ghost + Add-unit button stay
      // suppressed (no re-nudge); a group-blur then commits the bare building via
      // the shared resolve branch. Deleting only the "___" and retyping a different
      // designator ("Apt"→"Ste") leaves addr1 ≠ streetLine, so this won't fire —
      // that's correcting the unit type, not abandoning it.
      slotEngagedRef.current &&
      latestTopCandidate &&
      addressNeedsUnit(latestTopCandidate) &&
      latestTopCandidate.streetLine &&
      !currentAddr1.includes(UNIT_PLACEHOLDER) &&
      addr1Equivalent(currentAddr1, latestTopCandidate.streetLine)
    ) {
      setBareBuildingIntent(true);
    }
    const curIsVerified = isVerifiedRef.current;
    const curVerificationBaseline = verificationBaselineRef.current;
    const curIsUnverifiable = isUnverifiableRef.current;
    const curIsSkipped = isSkippedRef.current;
    const curOnChange = onChangeRef.current;
    // The breaker's reset teardown, shared by the two gestures that mean "this
    // address is gone, start fresh": clearing the field entirely (below) and a
    // wholesale street replacement (next). Both must land in the SAME state, so
    // the teardown lives in one place — strip verification meta, un-skip, drop
    // the breaker, the standardization callout, the unverifiable verdict, and
    // the bare-building/slot signals so the next address starts clean.
    const resetForFreshAddress = () => {
      if (curIsVerified || curIsSkipped) {
        curOnChange({ ...stripMeta({}), validationSkipped: "" });
      }
      if (autoVerificationOffRef.current) setAutoVerificationOff(false);
      if (standardizationRef.current) setStandardization(null);
      seenBeforeCommitRef.current = null;
      if (curIsUnverifiable) setIsUnverifiable(false);
      if (bareBuildingIntentRef.current) setBareBuildingIntent(false);
      slotEngagedRef.current = false;
      // A stale slot-fill verificationBaseline (a painted "…Apt ___") would
      // otherwise leave a verified baseline standing over the fresh street.
      if (verificationBaselineRef.current) setVerificationBaseline(null);
      canonicalizedStreetRef.current = null;
    };
    // Overwriting the street down to a single character: the user selected the
    // field (or backspaced it) down to one char and is starting over. The value
    // jumps straight from the old address to that char without ever passing
    // through "", so the empty-field reset below never sees it — but at one
    // character there is nothing left to protect, so this is the breaker's reset
    // gesture too. Keying on length === 1 (rather than diffing against the old
    // value) is what makes it robust: the engine's own writes are never one char
    // (canonical streets are long) and no mid-string edit collapses to one, so a
    // backspace-to-one or a select-all-then-type — whatever the typed char — is
    // the only way here. A select-all-then-type fires per keystroke, so its first
    // event IS this single char. Run BEFORE the reject-prefix check below: with a
    // standardization armed, that check would otherwise read the overwrite as a
    // rejection and trip the breaker instead of resetting it.
    if (currentAddr1.trim().length === 1) {
      resetForFreshAddress();
      return;
    }
    // Reject-an-auto-format — the manual equivalent of clicking undo. When the
    // engine auto-formatted the street in-session (`canonicalizedStreetRef` is
    // armed — set whether or not a "We standardized" callout showed, so this
    // also covers a SILENT forward-ghost / abbreviation completion) and this
    // edit makes that canonical street no longer a PREFIX of addr1, the user
    // altered the engine's canonical street (e.g. "W"→"west", "Main"→"Maple") —
    // a rejection. Trip the sticky breaker and mark the typed value unverifiable
    // so the engine never re-collapses to the candidate they just rejected.
    //
    // This is checked BEFORE the "autofill just landed" guard below on purpose:
    // a verify that landed WITHOUT changing a subfield (the user typed the exact
    // leaf) never ran this effect, so `prevVerifiedRef` stayed stale and the
    // guard would misclassify this real edit as the autofill landing. Running it
    // on the autofill's OWN write is a safe no-op — that write sets addr1 to the
    // canonical, which is still a prefix. The guard is stored UNIT-FREE
    // (`streetLine`), so correcting/appending a UNIT keeps the prefix → no trip.
    //
    // It does NOT gate on `curIsVerified`: the armed ref IS the signal (it's set
    // only when the engine canonicalized a street — a group-blur verify OR an
    // inline Tab/ArrowRight accept, which standardizes the street WITHOUT
    // verifying). So an inline-accept-then-edit-back trips even though isVerified
    // is false. The empty-street guard defers a full clear to the reset below.
    if (
      currentAddr1.trim() &&
      canonicalizedStreetRef.current &&
      !currentAddr1.startsWith(canonicalizedStreetRef.current)
    ) {
      setAutoVerificationOff(true);
      setStandardization(null);
      setVerificationBaseline(null);
      canonicalizedStreetRef.current = null;
      curOnChange({ ...stripMeta({}), validationSkipped: "1" });
      return;
    }
    // Autofill just landed (was unverified, now verified): leave the stash,
    // metadata, and any collapse-in-place standardization callout intact.
    if (!wasVerified && curIsVerified) {
      return;
    }
    // A verification reset landed alongside this subfield change (isVerified
    // was cleared in the same React batch as the value write — the ordinary-
    // edit strip below, or an undo). Not a fresh user edit; nothing to do.
    if (wasVerified && !curIsVerified) {
      return;
    }
    // Full clear of the address anchor: addr1 is now empty. The original reset
    // gesture — an empty field has nothing left to protect, so re-engage via
    // the shared teardown (its sibling, a wholesale street replacement, is
    // handled above before the reject-prefix check).
    if (!currentAddr1.trim()) {
      resetForFreshAddress();
      return;
    }
    // Ordinary user edit on a verified address: strip the stale verification
    // metadata so isVerified reflects the freshly-typed value, and dismiss any
    // standardization callout — the collapse it explained is being changed.
    // Crucially this does NOT set validationSkipped or trip the breaker:
    // verification stays ON and keeps helping while the user edits (fetch
    // re-enables the moment isVerified clears). Only a deliberate undo trips
    // the breaker. The `matchesBaseline` guard suppresses a redundant strip
    // when a change event fires but the value still equals the baseline.
    // Ordinary edit on a verified address that is NOT a rejection (the reject
    // case already tripped and returned above): strip the stale verification
    // metadata so isVerified reflects the freshly-typed value, and dismiss any
    // standardization callout. This keeps verification ON — the fetch re-enables
    // the moment isVerified clears. The `matchesBaseline` guard suppresses a
    // redundant strip when a change fires but the value still equals the baseline.
    const matchesBaseline =
      curVerificationBaseline &&
      ADDRESS_SUBFIELDS.every(
        (fieldName, fieldIndex) =>
          (current[fieldIndex] || "") ===
          (curVerificationBaseline[fieldName] || "")
      );
    if (curIsVerified && !matchesBaseline) {
      curOnChange(stripMeta({}));
      if (standardizationRef.current) setStandardization(null);
    }
    // Unverifiable is a verdict on the *prior* input — once the user edits,
    // drop it so autocomplete returns to silent-typing. Also clear
    // validationSkipped so the silent rule (which depends on !isSkipped) holds.
    if (curIsUnverifiable) {
      setIsUnverifiable(false);
      if (curIsSkipped) curOnChange({ validationSkipped: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the subfield primitives below are the intended triggers; the whole `value` object (read by snapshotCurrentSubfields) is just their sum, and resetSubfieldStash is a stable callback
  }, [
    value.addr1,
    value.city,
    value.state,
    value.zip,
    value.plus4,
    value.country,
  ]);

  // --- Undo a standardization -------------------------------------------
  //
  // Undo a collapse-in-place standardization: re-persist what the user saw
  // (their own spelling), mark the persisted value unverifiable, and trip the
  // breaker so the engine doesn't immediately re-collapse to the candidate they
  // just rejected. The breaker resets only on a full clear of the address.
  // Surfaces wire this to the standardization callout's undo button.
  //
  // Undo dismisses BOTH callouts: clearing the committed `isUnverifiable` drops
  // the unverifiable callout, and tripping the breaker closes `canFetch` so the
  // live `liveUnverifiable` signal drops too. The persisted address is still
  // unverifiable data (`validationSkipped="1"`), but undo is the user's "keep
  // what I typed" — re-nagging them with "couldn't verify" would be redundant.
  const onUndoStandardization = useCallback(() => {
    const payload = standardizationRef.current;
    if (!payload) return null;
    setStandardization(null);
    setVerificationBaseline(null);
    canonicalizedStreetRef.current = null;
    setIsUnverifiable(false);
    setAutoVerificationOff(true);
    const delta = {
      ...payload,
      isVerified: false,
      verificationProvider: "",
      verifiedAt: "",
      validationSkipped: "1",
    };
    onChange(delta);
    // The restored locality (city/state/zip) is the candidate's fill the user saw
    // only as GHOSTS — undo persists it, but it is NOT something the user typed.
    // Re-seed the stale-subfield bookkeeping to it so its effect doesn't read this
    // engine write as a user edit and latch it in `editedSubfieldsRef` (every other
    // locality-writing commit — group autofill, base-retain, bare-building — marks
    // its write the same way). Without this, an undo-filled state survives the
    // address being cleared and poisons the NEXT address: the contradiction gate
    // reads the orphaned value as a TYPED conflict and suppresses the fresh
    // candidate's locality ghosts. A genuinely typed city (no undo) is unaffected.
    // Re-seed from the RESULTING value (merge over the current one) so the baseline
    // matches whatever lands, even if the payload omits a subfield.
    const restored = { ...valueRef.current, ...delta };
    resetSubfieldStash({
      city: restored.city,
      state: restored.state,
      zip: restored.zip,
    });
    // Return the applied delta so a surface that OWNS its Save button can
    // persist this restored value in the SAME click (push-on-undo) instead of
    // dropping the user into a dirty state with the Save button back up. This
    // is the single source of truth for the undone value — consumers translate
    // it to their wire shape and POST it, never reconstruct it.
    return delta;
  }, [onChange, resetSubfieldStash]);

  // --- Consumer reset (Cancel / Save) -----------------------------------
  //
  // Clear every piece of in-session autocomplete state so the field group
  // behaves as if freshly opened on the current value. Surfaces call this when
  // they end an edit session — clearing the values alone (Cancel) or persisting
  // them (Save) is not enough: the hook's session refs/flags survive, and the
  // just-armed canonical street would make the NEXT address the user types read
  // as "edited the canonical away", trip the reject breaker, and kill
  // autocomplete. Refs/flags are cleared synchronously here; the baseline +
  // stale-subfield re-seed differs by case (`opts.deferred`):
  //
  //   - deferred (default — Cancel): a programmatic value REVERT is coming, so
  //     defer the re-seed to the subfield-change effect (`resetPendingRef`),
  //     which runs after the revert lands and re-seeds from the restored value.
  //   - immediate (Save): the current value IS the new committed baseline and no
  //     value change follows, so re-seed now. Arming `resetPendingRef` here would
  //     instead swallow the user's NEXT keystroke as a reset and leave isVerified
  //     standing — autocomplete would never re-engage.
  const reset = useCallback(
    ({ deferred = true, keepCallouts = false } = {}) => {
      setAutoVerificationOff(false);
      // A post-save reset (keepCallouts) leaves the standardization/unverifiable
      // callouts standing so the just-saved standardization stays undoable. On a
      // surface that owns its Save, Enter both commits the standardization and
      // drives Save in one keystroke; without this the save's reset would wipe
      // the callout the instant it appeared, leaving the user no way to undo a
      // reformat they never previewed. The next edit clears them via the
      // subfield-change effect. Cancel (the default) drops them immediately.
      if (!keepCallouts) {
        setStandardization(null);
        setIsUnverifiable(false);
      }
      setBareBuildingIntent(false);
      canonicalizedStreetRef.current = null;
      slotEngagedRef.current = false;
      seenBeforeCommitRef.current = null;
      if (deferred) {
        resetPendingRef.current = true;
        return;
      }
      const committed = valueRef.current;
      resetSubfieldStash({
        city: committed.city,
        state: committed.state,
        zip: committed.zip,
      });
      setVerificationBaseline(
        committed.isVerified === true
          ? snapshotCurrentSubfields(committed)
          : null
      );
    },
    [resetSubfieldStash]
  );

  // Verified status — true only while untouched since verification; editing
  // strips isVerified (see the subfield-change effect).
  const verified = isVerified;

  // USPS-Verified specifically — verified AND the verdict came from Smarty (the
  // backend's USPS audit), not an admin's manual override. This is what gates
  // the read-only "USPS Verified" badge: an admin-verified address is verified
  // but must NOT claim the USPS mark. An in-session engine verify always writes
  // the smarty provider (writeMeta), so the only "verified but not USPS" value
  // is a persisted admin override opened for edit.
  const uspsVerifiedRaw =
    isVerified && value.verificationProvider === VERIFICATION_PROVIDER;

  // Deliverability gate (opt-in via verifyDeliverability — only the OPTIMISTIC
  // surfaces set it; hold/reveal-on-save surfaces get a backend-corrected badge
  // from their save, so they don't need it). Autocomplete offers some addresses
  // as leaves that USPS DPV won't actually deliver (a No-Stat street record in a
  // PO-Box ZIP), so uspsVerifiedRaw over-claims. When gated, the badge waits for
  // the backend's real US Street verdict — the SAME check the save runs.
  //
  // Only for an IN-SESSION verification: a backend-verified address the user
  // opened but hasn't touched is already trusted — re-probing it would flicker
  // the badge off then on. Detect the false→true transition of uspsVerifiedRaw
  // synchronously (refs mutated during render, like isVerifiedRef above) so the
  // gate is live on the very commit render. A mount-verified address has no such
  // transition, so it's never gated; anything verified after an edit is.
  const prevUspsVerifiedRawRef = useRef(uspsVerifiedRaw);
  const verifiedInSessionRef = useRef(false);
  if (uspsVerifiedRaw && !prevUspsVerifiedRawRef.current) {
    verifiedInSessionRef.current = true;
  }
  prevUspsVerifiedRawRef.current = uspsVerifiedRaw;

  const deliverabilityGateActive =
    verifyDeliverability && uspsVerifiedRaw && verifiedInSessionRef.current;
  const { deliverable } = useDeliverabilityVerdict({
    address: value,
    enabled: deliverabilityGateActive,
  });
  // Show the badge ONLY once the backend CONFIRMS deliverable (true). Fail
  // CLOSED on everything else: while the probe is in flight (undefined) and when
  // it's unreachable (null = outage) the badge stays HIDDEN. These are the
  // OPTIMISTIC surfaces — there's no backend-correcting save in the visible flow
  // to walk back an over-claim — so a "USPS Verified" badge we can't stand behind
  // must never show. We'd rather withhold a real verification during an outage
  // than assert one we didn't confirm; the backend's own check still verifies a
  // valid address on save. The cost is that a deliverable badge appears a beat
  // after blur (the probe round-trip) instead of instantly; a during-typing
  // pre-fetch would make it instant by having the verdict already in hand.
  const deliverabilityShowsBadge =
    !deliverabilityGateActive || deliverable === true;
  const uspsVerified = uspsVerifiedRaw && deliverabilityShowsBadge;

  // Multi-unit inline fill affordance. Shown when the top candidate's addr1
  // ghost carries the unit placeholder ("…Apt ___"): the building is known but
  // the unit isn't. Clicking paints the candidate (city/state/zip + the unit
  // sentinel) and the slot-select effect highlights the "___" so the user can
  // type their unit immediately. This is the ONLY inline button — there is no
  // general click-to-commit for an ordinary address.
  //
  // It is a START affordance for the bare building only: the `enteredUnitSlot`
  // guard hides it the moment the user types into the slot — at which point the
  // addr1 ghost is suppressed too (see `shadows`), so the button and the ghost
  // disappear together and the user is never re-nudged toward a unit in
  // progress (or one they've abandoned via the bare-building breaker).
  const multiUnitFill = useMemo(
    () => ({
      show:
        Boolean(shadows.addr1) &&
        shadows.addr1.includes(UNIT_PLACEHOLDER) &&
        !enteredUnitSlot,
      onFill: () => resolveOnGroupBlurRef.current(false, true),
    }),
    [shadows.addr1, enteredUnitSlot]
  );

  // Live "nothing usable" signal — latched so the callout doesn't flash off
  // between keystrokes. Updates only when a fetch *settles* (debounce drained
  // + query no longer fetching). On settle it fires for either of two shapes,
  // neither of which subsumes the other:
  //   - NO COMPLETING CANDIDATE (`!topCandidate`) — the did-you-mean shape:
  //     Smarty returned rows but none completes the typed street, so
  //     `allCandidates` is NON-empty while `topCandidate` is null. (Keying on
  //     `allCandidates.length === 0` alone misses this — the regression that
  //     made "8602 w grand test"-style streets wait for blur.)
  //   - ZERO RAW CANDIDATES (`allCandidates.length === 0`) — including the
  //     append-a-unit case, where the full-string fetch is empty yet a
  //     RETAINED base still supplies `topCandidate` (so `!topCandidate` alone
  //     would miss it). The retained locality ghost and the callout coexist.
  // There is NO city/zip structural-completeness gate — a settled fetch with
  // nothing usable shows the callout IMMEDIATELY, while the user is editing.
  // While a new fetch is in flight the prior verdict sticks, so typing to
  // refine keeps the callout visible until the next result changes the
  // picture. Cleared whenever canFetch closes (verified / skipped-blocked /
  // regex broken). Distinct from `isUnverifiable`, the committed verdict set
  // inside `resolveOnGroupBlur`.
  const [liveUnverifiable, setLiveUnverifiable] = useState(false);
  useEffect(() => {
    if (!canFetch) {
      setLiveUnverifiable(false);
      return;
    }
    // Smarty outage: an errored fetch is not a "couldn't verify" answer about
    // the address — suppress the callout so the outage stays invisible. (canFetch
    // stays true through an outage so the flow can recover, so this needs its
    // own guard here.)
    if (autocompleteUnavailable) {
      setLiveUnverifiable(false);
      return;
    }
    if (fetchInFlight) return;
    setLiveUnverifiable(!topCandidate || allCandidates.length === 0);
  }, [
    canFetch,
    autocompleteUnavailable,
    fetchInFlight,
    topCandidate,
    allCandidates,
  ]);

  return {
    // Echoed inputs — re-exposed so a single autocomplete object carries
    // everything the field wrappers need from context (value for input
    // values, onChange for delta writes, refs to attach to inputs). The
    // containerRef rides along so `AddressAutocompleteRoot` can read it
    // from the same object the wrappers pull from.
    value,
    onChange,
    addr1Ref,
    cityRef,
    zipRef,
    containerRef,
    shadows,
    // Verified status (any provider, incl. admin override). True only while
    // untouched since verification.
    verified,
    // Verified by Smarty specifically → drives the read-only USPS-Verified
    // badge. An admin override is `verified` but NOT `uspsVerified`.
    uspsVerified,
    // The collapse-in-place standardization undo payload (null when none) and
    // its undo action → standardization callout.
    standardization,
    onUndoStandardization,
    // Multi-unit inline fill button { show, onFill } → ShadowField's slot.
    multiUnitFill,
    // Live "fetch settled, nothing usable" → unverifiable callout while editing.
    liveUnverifiable,
    // Committed unverifiable verdict (set inside resolveOnGroupBlur).
    isUnverifiable,
    // Smarty outage (the fetch is erroring): the whole flow is the invisible
    // OFF state — no verdict is stamped and no callout shows. Exposed so a
    // surface whose own submit gate REQUIRES a verdict (e.g. Sacrament
    // Tracking's yup verify-or-skip-when-dirty) can tell that no verdict can be
    // produced and let the address through, exactly as it does for non-US.
    autocompleteUnavailable,
    onFieldBlur,
    // Freeze the visible ghosts (typed text + each field's gray suffix) into the
    // value as plain black text, no verification metadata — the hold-mode Enter
    // commit. Own-Save surfaces also call this at the start of their Save so a
    // click freezes the ghost exactly as Enter does (the canonical still lands
    // via the surface's standardized POST).
    commitVisibleGhosts,
    // Reset all in-session autocomplete state to the just-opened baseline.
    // Surfaces with a Cancel button call this alongside reverting their values.
    reset,
    // Explicit synchronous resolve for callers that need to gate on the
    // verdict (e.g. a Save button that must know whether to POST or surface
    // the Unverifiable badge before committing). Returns the verdict directly
    // so the caller can branch without waiting for the React commit that the
    // state writes trigger. Callers should still wait for `fetchInFlight` to be
    // false before calling, or accept a `{ kind: "deferred" }` verdict.
    resolveOnGroupBlur,
    fetchInFlight,
  };
}

// Build a baseline snapshot from current `value` overlaid with `filled` —
// the same merged shape onChange(writeMeta(filled)) is about to commit.
// Used by the verification paths (autofill + pick) so the baseline
// reflects post-autofill state without waiting for the next render.
function snapshotBaseline(value, filled) {
  return ADDRESS_SUBFIELDS.reduce((acc, fieldName) => {
    acc[fieldName] =
      (filled[fieldName] !== undefined
        ? filled[fieldName]
        : value[fieldName]) || "";
    return acc;
  }, {});
}

export {
  STREET_REGEX,
  VERIFICATION_PROVIDER,
  ADDRESS_SUBFIELDS,
  DEBOUNCE_MS,
  SHADOW_FIELDS,
};
